import { useState, useEffect } from 'react';
import { track } from '@vercel/analytics';
import { Analytics } from '@vercel/analytics/react';
import './App.css';
import ScorecardCard from './components/ScorecardCard';
import { lookupSalary } from './data/salaryDatabase';
import { toRoleSlug, getQuestionsForRole, isDefaultQuestions, DEFAULT_QUESTIONS } from './questionLibrary';
import { getCriteriaForRole } from './criteriaLibrary';
import { generateHiringKitPDF, getHiringKitPDFBase64, WARMUP_QUESTIONS } from './utils/generatePDF';
import { supabase } from './utils/supabase';
import Success from './pages/Success';

function Navbar({ scrolled, onCtaClick }) {
  return (
    <nav style={{
      position: 'sticky',
      top: 0,
      zIndex: 100,
      background: '#FFFFFF',
      height: 64,
      display: 'flex',
      alignItems: 'center',
      borderBottom: scrolled ? '1px solid #EEEEEE' : '1px solid transparent',
      transition: 'border-color 0.2s ease',
    }}>
      <div style={{
        maxWidth: 1120,
        margin: '0 auto',
        padding: '0 24px',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <span style={{
          fontFamily: 'Inter, sans-serif',
          fontWeight: 700,
          fontSize: 20,
          color: '#1A7A6E',
        }}>
          RoleReady
        </span>
        <button
          onClick={onCtaClick}
          style={{
            background: '#B8860B',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: 6,
            height: 40,
            padding: '0 20px',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 600,
            fontSize: 15,
            cursor: 'pointer',
          }}
        >
          Get started
        </button>
      </div>
    </nav>
  );
}

function SampleKitModal({ isOpen, onClose }) {
  const [weakOpen1, setWeakOpen1] = useState(false);
  const [weakOpen2, setWeakOpen2] = useState(false);
  const [weakOpen3, setWeakOpen3] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const sectionHeading = (text, mt) => (
    <h3 style={{
      fontFamily: 'Inter, sans-serif',
      fontSize: 16,
      fontWeight: 700,
      color: '#1A7A6E',
      borderBottom: '2px solid #1A7A6E',
      paddingBottom: 6,
      marginBottom: 16,
      marginTop: mt || 0,
    }}>{text}</h3>
  );

  const callout = (text) => (
    <div style={{
      background: '#F0FDF4',
      border: '1px solid #BBF7D0',
      borderRadius: 8,
      padding: '14px 16px',
      marginTop: 16,
      fontFamily: 'Inter, sans-serif',
      fontSize: 13,
      color: '#15803D',
      lineHeight: 1.6,
    }}>{text}</div>
  );

  const categoryBadge = (text) => (
    <span style={{
      background: '#EAF4F2',
      color: '#1A7A6E',
      fontSize: 11,
      fontWeight: 600,
      borderRadius: 4,
      padding: '2px 7px',
      fontFamily: 'Inter, sans-serif',
    }}>{text}</span>
  );

  const renderQuestion = (q, weakText, weakOpen, setWeakOpen) => (
    <div style={{ marginBottom: 20 }}>
      {categoryBadge(q.badge)}
      <div style={{ fontWeight: 600, fontSize: 14, color: '#1A1A1A', margin: '8px 0', fontFamily: 'Inter, sans-serif' }}>{q.question}</div>
      <div style={{ background: '#F5FAF9', borderLeft: '3px solid #1A7A6E', padding: '10px 12px', borderRadius: '0 6px 6px 0', marginBottom: 8 }}>
        <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 11, fontWeight: 700, color: '#1A7A6E', textTransform: 'uppercase', marginBottom: 4 }}>WHAT A STRONG ANSWER SOUNDS LIKE</div>
        <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: '#333', lineHeight: 1.6 }}>{q.strongText}</div>
      </div>
      <div style={{ background: '#FFF8EC', borderLeft: '3px solid #B8860B', padding: '10px 12px', borderRadius: '0 6px 6px 0', marginBottom: 8 }}>
        <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 11, fontWeight: 700, color: '#B8860B', textTransform: 'uppercase', marginBottom: 4 }}>WATCH FOR THE REHEARSED ANSWER</div>
        <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: '#333', lineHeight: 1.6 }}>{q.watchText}</div>
      </div>
      <button
        onClick={() => setWeakOpen(!weakOpen)}
        style={{ background: 'none', border: 'none', fontFamily: 'Inter, sans-serif', fontSize: 12, color: '#888888', cursor: 'pointer', padding: 0, marginBottom: weakOpen ? 4 : 0 }}
      >
        {weakOpen ? '▴ Hide weak answer' : 'What a weak answer looks like'}
      </button>
      {weakOpen && (
        <div style={{ background: '#FFF5F5', borderLeft: '3px solid #CC4444', padding: '10px 12px', borderRadius: '0 6px 6px 0' }}>
          <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: '#333', lineHeight: 1.6 }}>{weakText}</div>
        </div>
      )}
    </div>
  );

  const anchorCell = (bg, labelColor, label, bullets) => (
    <div style={{ background: bg, padding: '8px 10px', borderRadius: 4 }}>
      <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 11, fontWeight: 700, color: labelColor, marginBottom: 4 }}>{label}</div>
      {bullets.map((b, i) => (
        <div key={i} style={{ fontFamily: 'Inter, sans-serif', fontSize: 11, color: '#444', lineHeight: 1.5 }}>• {b}</div>
      ))}
    </div>
  );

  const renderCriterion = (label, anchors) => (
    <div style={{ marginBottom: 16 }}>
      <div style={{ background: '#EBF5FB', borderLeft: '2px solid #1B4F72', padding: '8px 12px', fontWeight: 700, fontSize: 13, color: '#1B4F72', marginBottom: 2, fontFamily: 'Inter, sans-serif' }}>{label}</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, marginBottom: 8 }}>
        {anchorCell('#FADBDB', '#C0392B', 'No (1)', anchors.no)}
        {anchorCell('#FDEBD0', '#E67E22', 'Maybe (2)', anchors.maybe)}
        {anchorCell('#D5F5E3', '#1A7A6E', 'Yes (3)', anchors.yes)}
        {anchorCell('#D6EAF8', '#1B4F72', 'Strong Yes (4)', anchors.strong)}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontFamily: 'Inter, sans-serif', fontSize: 12, color: '#555555' }}>
        <span style={{ fontWeight: 600 }}>Rating:</span>
        {['No (1)', 'Maybe (2)', 'Yes (3)', 'Strong yes (4)'].map((opt) => (
          <span key={opt} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ display: 'inline-block', width: 10, height: 10, border: '1px solid #BBBBBB', borderRadius: 2 }} />
            {opt}
          </span>
        ))}
      </div>
    </div>
  );

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2000,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#FFFFFF',
          borderRadius: 12,
          maxWidth: 720,
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          position: 'relative',
          fontFamily: 'Inter, sans-serif',
        }}
      >
        {/* Sticky header */}
        <div style={{
          position: 'sticky',
          top: 0,
          background: '#FFFFFF',
          borderBottom: '1px solid #EEEEEE',
          padding: '16px 24px',
          zIndex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <span style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 16, color: '#1A7A6E' }}>RoleReady</span>
          <span style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: '#555555' }}>Sample Kit - Cafe Manager · Auckland</span>
          <button onClick={onClose} style={{ fontSize: 20, color: '#888888', background: 'none', border: 'none', cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        {/* Framing strip */}
        <div style={{
          background: '#F5F5F5',
          padding: '12px 24px',
          borderBottom: '1px solid #EEEEEE',
          fontFamily: 'Inter, sans-serif',
          fontSize: 13,
          color: '#555555',
          lineHeight: 1.6,
        }}>
          This is a real sample, same format as your kit, built for a Cafe Manager in Auckland. Your kit is generated for your exact role, region, and business context, and includes the full question set and coaching notes.
        </div>

        {/* Panel body */}
        <div style={{ padding: 24 }}>

          {/* SECTION C — JOB AD */}
          {sectionHeading('Section 01 - Job Ad', 32)}

          <div style={{ background: '#F9F9F9', border: '1px solid #E5E5E5', borderRadius: 8, padding: 20 }}>
            <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 11, fontWeight: 700, color: '#888888', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>JOB TITLE</div>
            <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 14, color: '#1A1A1A', marginBottom: 14 }}>Cafe Manager - Auckland</div>
            <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 11, fontWeight: 700, color: '#888888', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4, marginTop: 14 }}>SEARCH RESULT HIGHLIGHTS</div>
            <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 12, color: '#777777', fontStyle: 'italic', marginBottom: 6 }}>These 3 points appear in SEEK search results before candidates click your ad.</div>
            {['✓ Small team, no head-office interference', '✓ Consistent covers, loyal customer base', '✓ Real autonomy to run your shifts your way'].map((item, i) => (
              <div key={i} style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: '#1A1A1A', lineHeight: 1.6 }}>{item}</div>
            ))}
            <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 11, fontWeight: 700, color: '#888888', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4, marginTop: 14 }}>ABOUT THE ROLE</div>
            <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: '#1A1A1A', lineHeight: 1.6 }}>Small cafe in central Auckland, 200+ covers a day, tight team of six. We need a Cafe Manager who can hold standards without being held by the hand. If you prefer someone above you to refer every decision to, this is not the right fit. Here you own the floor from open to close.</div>
          </div>

          {callout('Your full job ad includes all sections formatted for SEEK NZ or Trade Me Jobs, ready to paste and post.')}

          {/* SECTION D — PAY BENCHMARK */}
          {sectionHeading('Section 02 - NZ Pay Benchmark', 32)}

          <div style={{ background: '#F9F9F9', border: '1px solid #E5E5E5', borderRadius: 8, padding: 20 }}>
            <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 32, fontWeight: 700, color: '#1A7A6E' }}>$28/hr</div>
            <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: '#555555', marginBottom: 8 }}>per hour</div>
            <div style={{ height: 1, background: '#EEEEEE', margin: '8px 0' }} />
            <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 14, fontWeight: 600, color: '#1A1A1A' }}>$24/hr – $34/hr</div>
            <div style={{ height: 1, background: '#EEEEEE', margin: '8px 0' }} />
            <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: '#555555' }}>Confidence: High</div>
            <div style={{ height: 1, background: '#EEEEEE', margin: '8px 0' }} />
            <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 11, color: '#999999', fontStyle: 'italic' }}>RoleReady NZ Pay Ranges · June 2026 review</div>
          </div>

          {callout("Your kit includes the median rate, full range, and confidence rating for your exact role and region, so you can make your offer without second-guessing.")}

          {/* SECTION A — INTERVIEW PACK */}
          {sectionHeading('Section 03 - Interview Pack')}

          <div style={{ background: '#1A1A1A', borderRadius: 6, padding: '12px 16px', marginBottom: 20 }}>
            <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 11, fontWeight: 700, color: '#888888', letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: 6 }}>CHARACTER PRESSURE FOR THIS HIRE</div>
            <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 14, color: '#FFFFFF', fontStyle: 'italic' }}>Holding standards with people you have a relationship with.</div>
          </div>

          {renderQuestion(
            {
              badge: 'ROLE COMPETENCY',
              question: 'Walk me through how you would open the cafe on a busy Monday morning.',
              strongText: 'They walk you through a real routine. What they check first, how they brief the team, what they do if something is already wrong. The detail is specific, not general. You can tell they have actually opened a cafe before.',
              watchText: 'A smooth answer about checking in with the team and making sure everything is ready. The tell: no specifics, no order, no moment where something was wrong and they fixed it.',
            },
            'They describe a vague morning routine with no real sequence. No mention of what they actually check, who they brief, or what could go wrong.',
            weakOpen1,
            setWeakOpen1,
          )}

          {renderQuestion(
            {
              badge: 'ROLE COMPETENCY',
              question: 'How do you handle a staff member who is consistently late or underperforming?',
              strongText: 'They describe a real conversation they had. What they said, how the person responded, and what they did next. They are honest that it was uncomfortable. They did not let it slide because the person was well-liked.',
              watchText: 'A perfect answer about having a private chat and setting clear expectations, with no tension and no real outcome. The polished version describes the process without showing they have ever actually sat across from someone difficult.',
            },
            'They say they would have a quiet word and hope it improves, or admit they tend to avoid those conversations until things blow up.',
            weakOpen2,
            setWeakOpen2,
          )}

          {renderQuestion(
            {
              badge: 'VALUES & CULTURE',
              question: 'Tell me about a time you made a mistake at work. What happened and how did you handle it?',
              strongText: 'They name a real mistake with real consequences, own it without blaming others, explain how they fixed it, and what changed afterwards. It sounds a bit uncomfortable to tell, which is a good sign.',
              watchText: 'A rehearsed humble-brag, the mistake is secretly a strength, like caring too much or trusting someone too much. The tell: no real cost, no genuine discomfort in the telling.',
            },
            "They give a non-mistake like working too hard, or describe an error that was really someone else's fault.",
            weakOpen3,
            setWeakOpen3,
          )}

          {callout('Your full kit includes all 8 questions for library roles (10 for generated roles), each with a strong answer guide, watch-for coaching note, and weak answer, built for your exact role.')}

          {/* SECTION B — SCORECARD */}
          {sectionHeading('Section 04 - Candidate Scorecard', 32)}

          {renderCriterion('Shift management and team standards', {
            no: ['Cannot describe how they run a shift without a manager present.', 'Gives vague answers about keeping things running smoothly with no detail.'],
            maybe: ['Describes covering shifts but always with someone senior nearby.', 'Some examples of leading but unclear who was actually in charge.'],
            yes: ['Gives a clear example of running a shift independently and handling what came up.', 'Describes their own checklist or routine for opening or closing.'],
            strong: ['Has designed shift procedures that others now follow.', 'Ran the venue solo for extended periods with full accountability.'],
          })}

          {renderCriterion('Handling difficult customers and complaints', {
            no: ['Cannot recall a specific example of dealing with an upset customer.', 'Says they would get the owner to handle any complaints.'],
            maybe: ['Describes one example but outcome was unclear or handled by someone else.', 'Shows some empathy but no clear process for resolving issues.'],
            yes: ['Gives a specific example with clear steps taken to resolve the complaint.', 'Shows they listen first and take responsibility for finding a solution.'],
            strong: ['Has created or improved complaint handling processes for previous employers.', 'Describes training other staff in how to handle difficult situations.'],
          })}

          {callout("Your full scorecard includes 5 role-calibrated criteria, each with No / Maybe / Yes / Strong Yes anchor descriptions, so you're rating evidence, not instinct. Plus a candidate comparison sheet.")}

          {/* SECTION E — CLOSING CTA */}
          <div style={{ marginTop: 32, paddingTop: 24, borderTop: '1px solid #EEEEEE', textAlign: 'center' }}>
            <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 18, fontWeight: 700, color: '#1A1A1A', marginBottom: 8 }}>Ready to build your kit?</div>
            <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 14, color: '#555555', marginBottom: 16 }}>Free to start. Your kit, built for your exact role, takes about 15 minutes.</div>
            <button
              onClick={() => { onClose(); window.location.href = '/?start=1'; }}
              style={{
                background: '#B8860B',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: 6,
                padding: '14px 32px',
                fontFamily: 'Inter, sans-serif',
                fontSize: 16,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Get started free →
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}

function HeroSection({ onCtaClick }) {
  const [activeTab, setActiveTab] = useState('interview');
  const [sampleOpen, setSampleOpen] = useState(false);

  const tabs = [
    { id: 'job', label: 'Job Ad' },
    { id: 'interview', label: 'Interview Pack' },
    { id: 'scorecard', label: 'Scorecard' },
  ];

  return (
    <>
      <SampleKitModal isOpen={sampleOpen} onClose={() => setSampleOpen(false)} />
      <section className="hero-section" style={{
        background: '#FFFFFF',
        padding: '80px 24px 64px',
      }}>
      <div
        className="hero-grid"
        style={{
          maxWidth: 1120,
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
          gap: 64,
          alignItems: 'center',
        }}
      >
        {/* Left column — copy and CTA */}
        <div className="hero-copy">
          <h1
            className="hero-h1 hero-animate hero-delay-0"
            style={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: 'clamp(32px, 3.8vw, 48px)',
              lineHeight: 1.1,
              color: '#1A1A1A',
              margin: '0 0 20px',
              letterSpacing: '-0.8px',
            }}
          >
            Hiring sorted properly.<br className="hero-h1-br" /> In 15 minutes.
          </h1>
          <p
            className="hero-sub hero-animate hero-delay-1"
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: 18,
              lineHeight: 1.6,
              color: '#555555',
              margin: '0 0 28px',
            }}
          >
            Everything you need to choose the right person with confidence.
          </p>
          <div className="hero-cta hero-animate hero-delay-2" style={{ marginBottom: 0 }}>
            <button
              onClick={onCtaClick}
              style={{
                background: '#B8860B',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: 6,
                height: 52,
                padding: '0 32px',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 600,
                fontSize: 17,
                cursor: 'pointer',
                letterSpacing: '-0.1px',
              }}
            >
              Get started free →
            </button>
            <p style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: 14,
              fontWeight: 600,
              color: '#6B7280',
              marginTop: 12,
              marginBottom: 0,
              textAlign: 'left',
            }}>
              Free to start · Full hiring kit $29 · No subscription
            </p>
            <button data-sample-modal onClick={() => setSampleOpen(true)} style={{ background: 'none', border: 'none', fontFamily: 'Inter, sans-serif', fontSize: 14, color: '#1A7A6E', cursor: 'pointer', textDecoration: 'underline', padding: 0, marginTop: 8, display: 'block' }}>See a sample kit →</button>
          </div>
        </div>

        {/* Right column — rebuilt sample-output panel */}
        <div
          className="hero-preview hero-animate hero-delay-3"
          style={{
            background: '#F9F9F9',
            border: '1px solid #E5E5E5',
            borderRadius: 12,
            padding: 28,
            boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
          }}
        >
          {/* Header */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 11, fontWeight: 500, color: '#888888' }}>
              Sample output - your role will look like this
            </div>
            <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 11, color: '#AAAAAA', marginTop: 2 }}>
              Generated for: Cafe Manager · Auckland
            </div>
          </div>

          {/* Tab bar */}
          <div className="preview-tabs" style={{
            display: 'flex',
            borderBottom: '1px solid #E5E5E5',
            marginBottom: 16,
          }}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  flex: 1,
                  background: 'none',
                  border: 'none',
                  borderBottom: activeTab === tab.id ? '2px solid #1A7A6E' : '2px solid transparent',
                  padding: '8px 4px',
                  minHeight: 44,
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 12,
                  fontWeight: activeTab === tab.id ? 600 : 400,
                  color: activeTab === tab.id ? '#1A7A6E' : '#777777',
                  cursor: 'pointer',
                  marginBottom: -1,
                  whiteSpace: 'nowrap',
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab: Job Ad */}
          {activeTab === 'job' && (
            <div>
              <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 15, fontWeight: 700, color: '#1A1A1A', marginBottom: 3, lineHeight: 1.3 }}>
                Cafe Manager - Full Time
              </div>
              <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 12, color: '#555555', marginBottom: 10 }}>
                Auckland CBD · Full Time
              </div>
              <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, lineHeight: 1.6, color: '#444444', marginBottom: 10 }}>
                Small café in central Auckland, 250+ covers a day. Looking for a manager who can run the floor and hold standards without hand-holding.
              </div>
              <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 10, fontWeight: 700, color: '#888888', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>
                What you'll do
              </div>
              {[
                'Lead daily operations, open, close, floor, and team',
                'Manage rostering, stock, and supplier relationships',
                'Hold quality and service standards across every shift',
              ].map((b, i) => (
                <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
                  <span style={{ color: '#1A7A6E', fontSize: 13, lineHeight: 1.5, flexShrink: 0 }}>•</span>
                  <span style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, lineHeight: 1.5, color: '#444444' }}>{b}</span>
                </div>
              ))}
              <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #E8E8E8', display: 'flex', alignItems: 'center', gap: 6 }}>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="#1A7A6E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                <span style={{ fontFamily: 'Inter, sans-serif', fontSize: 11, color: '#1A7A6E' }}>Built to align with NZ employment law</span>
              </div>
              <div style={{ textAlign: 'right', fontFamily: 'Inter, sans-serif', fontSize: 10, color: '#BBBBBB', marginTop: 6 }}>Preview of full output</div>
            </div>
          )}

          {/* Tab: Interview Pack — default active, shows coaching format */}
          {activeTab === 'interview' && (
            <div>
              <div style={{ marginBottom: 10 }}>
                <span style={{ fontFamily: 'Inter, sans-serif', fontSize: 10, fontWeight: 700, color: '#1A7A6E', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                  Q2 · Role competency
                </span>
              </div>
              <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 14, fontWeight: 600, color: '#1A1A1A', lineHeight: 1.4, marginBottom: 14 }}>
                Walk me through a time you turned around an underperforming shift.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {/* Strong answer */}
                <div style={{ background: '#F0FDF4', borderLeft: '3px solid #15803D', borderRadius: '0 6px 6px 0', padding: '8px 10px' }}>
                  <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 10, fontWeight: 700, color: '#15803D', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 3 }}>
                    Strong answer
                  </div>
                  <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 12, color: '#374151', lineHeight: 1.5 }}>
                    Names the specific problem, the action they took, and the measurable shift in the team.
                  </div>
                </div>
                {/* Watch for — visually prominent key differentiator */}
                <div style={{ background: '#FFF8EC', borderLeft: '3px solid #B8860B', borderRadius: '0 6px 6px 0', padding: '10px 10px' }}>
                  <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 10, fontWeight: 700, color: '#B8860B', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 3 }}>
                    Watch for the rehearsed answer
                  </div>
                  <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 12, color: '#374151', lineHeight: 1.5 }}>
                    A perfectly rehearsed answer that sounds confident but contains no real detail about the actual people or situation involved.
                  </div>
                </div>
                {/* Weak answer */}
                <div style={{ background: '#F5F5F5', borderLeft: '3px solid #CCCCCC', borderRadius: '0 6px 6px 0', padding: '8px 10px' }}>
                  <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 10, fontWeight: 700, color: '#777777', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 3 }}>
                    Weak answer
                  </div>
                  <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 12, color: '#374151', lineHeight: 1.5 }}>
                    Vague. Describes what they'd do hypothetically, not what they actually did.
                  </div>
                </div>
              </div>
              <div style={{ textAlign: 'right', fontFamily: 'Inter, sans-serif', fontSize: 10, color: '#BBBBBB', marginTop: 8 }}>Preview of full output</div>
            </div>
          )}

          {/* Tab: Scorecard */}
          {activeTab === 'scorecard' && (
            <div>
              <div style={{ background: '#EBF5FB', borderLeft: '2px solid #1B4F72', borderRadius: '0 4px 4px 0', padding: '6px 10px', fontFamily: 'Inter, sans-serif', fontSize: 13, fontWeight: 700, color: '#1B4F72', marginBottom: 10 }}>
                Team leadership and standards
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, marginBottom: 12 }}>
                {[
                  { label: 'No (1)', bg: '#FADBDB', color: '#C0392B', text: 'Cannot describe running a shift without a manager present.' },
                  { label: 'Maybe (2)', bg: '#FDEBD0', color: '#E67E22', text: 'Has covered shifts but always with someone senior nearby.' },
                  { label: 'Yes (3)', bg: '#D5F5E3', color: '#1A7A6E', text: 'Gives a clear example of running a shift independently and handling what came up.' },
                  { label: 'Strong Yes (4)', bg: '#D6EAF8', color: '#1B4F72', text: 'Has designed shift procedures that others now follow.' },
                ].map((cell, i) => (
                  <div key={i} style={{ background: cell.bg, padding: '6px 8px', borderRadius: 4 }}>
                    <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 10, fontWeight: 700, color: cell.color, marginBottom: 3 }}>{cell.label}</div>
                    <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 11, color: '#444444', lineHeight: 1.4 }}>{cell.text}</div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 8 }}>
                {[
                  { label: 'No', active: false },
                  { label: 'Maybe', active: false },
                  { label: 'Yes', active: true },
                  { label: 'Strong yes', active: false },
                ].map((pill) => (
                  <div key={pill.label} style={{
                    padding: '4px 9px',
                    borderRadius: 4,
                    border: pill.active ? '2px solid #1A7A6E' : '1px solid #E0E0E0',
                    background: pill.active ? '#EAF4F2' : '#FAFAFA',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: 11,
                    fontWeight: pill.active ? 700 : 400,
                    color: pill.active ? '#1A7A6E' : '#888888',
                  }}>
                    {pill.label}
                  </div>
                ))}
              </div>
              <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 11, color: '#888888', fontStyle: 'italic' }}>
                Same criteria, every candidate. Your decision stays consistent.
              </div>
              <div style={{ textAlign: 'right', fontFamily: 'Inter, sans-serif', fontSize: 10, color: '#BBBBBB', marginTop: 8 }}>Preview of full output</div>
            </div>
          )}
        </div>
      </div>

        <div
          className="hero-hooks-inline"
          style={{
            maxWidth: 1120,
            margin: '40px auto 0',
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '0 48px',
            paddingTop: 32,
            borderTop: '1px solid #EEEEEE',
          }}
        >
          {[
            'Know what to ask, and what a strong answer actually sounds like.',
            'No HR team needed. You will know what to do at every step.',
            'Compare candidates on evidence, not on who interviewed best.',
          ].map((hook, i) => (
            <p key={i} style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: 14,
              fontWeight: 500,
              color: '#555555',
              margin: 0,
              lineHeight: 1.6,
              paddingLeft: i === 0 ? 0 : 24,
              borderLeft: i === 0 ? 'none' : '1px solid #E0E0E0',
            }}>
              {hook}
            </p>
          ))}
        </div>

      <style>{`
        @keyframes heroFadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .hero-animate {
          animation: heroFadeIn 0.5s ease both;
        }
        .hero-delay-0 { animation-delay: 0ms; }
        .hero-delay-1 { animation-delay: 80ms; }
        .hero-delay-2 { animation-delay: 160ms; }
        .hero-delay-3 { animation-delay: 240ms; }
        @media (prefers-reduced-motion: reduce) {
          .hero-animate { animation: none !important; }
        }
        @media (max-width: 768px) {
          .hero-section {
            padding: 40px 20px 48px !important;
          }
          .hero-grid {
            display: flex !important;
            flex-direction: column !important;
            gap: 24px !important;
          }
          .hero-copy,
          .hero-preview {
            width: 100% !important;
          }
          .hero-h1 {
            margin-bottom: 12px !important;
            font-size: 28px !important;
          }
          .hero-h1-br { display: none; }
          .hero-sub {
            font-size: 16px !important;
            margin-bottom: 20px !important;
          }
          .hero-cta {
            margin-bottom: 24px !important;
          }
          .hero-preview {
            padding: 16px !important;
          }
          .preview-tabs button {
            min-height: 44px !important;
            font-size: 11px !important;
            padding: 8px 2px !important;
          }
          nav button {
            font-size: 13px !important;
            padding: 0 14px !important;
            height: 34px !important;
          }
          .hero-hooks-inline {
            grid-template-columns: 1fr !important;
            gap: 16px !important;
            margin-top: 28px !important;
          }
          .hero-hooks-inline p {
            padding-left: 0 !important;
            border-left: none !important;
            padding-top: 16px !important;
            border-top: 1px solid #EEEEEE !important;
          }
          .hero-hooks-inline p:first-child {
            padding-top: 0 !important;
            border-top: none !important;
          }
        }
      `}</style>
      </section>
    </>
  );
}

function HooksSection() {
  return (
    <section style={{ background: '#F5F5F5', padding: '52px 24px' }}>
      <div style={{ maxWidth: 1120, margin: '0 auto' }}>
        <p style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: 12,
          fontWeight: 700,
          color: '#1A7A6E',
          textTransform: 'uppercase',
          letterSpacing: '0.8px',
          margin: '0 0 32px',
        }}>
          How it works
        </p>
        <div
          className="process-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '0',
            position: 'relative',
          }}
        >
          {[
            {
              number: '01',
              title: 'Tell us the role',
              body: 'Just the job title and where you are. That\'s the only thing we actually need.',
            },
            {
              number: '02',
              title: 'Add a bit of context',
              body: 'A line or two about your team or what makes the role different. Optional, but it makes everything sharper.',
            },
            {
              number: '03',
              title: 'We do the work',
              body: 'We build the interview questions, the scorecard, the job ad, and the pay benchmark, all calibrated to that role.',
            },
            {
              number: '04',
              title: 'You get your kit',
              body: 'A complete hiring kit in about 15 minutes, formatted and ready to use.',
            },
          ].map((step, i) => (
            <div
              key={i}
              style={{
                paddingRight: 32,
              }}
            >
              <div style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 28,
                fontWeight: 700,
                color: '#E0E0E0',
                lineHeight: 1,
                marginBottom: 12,
              }}>
                {step.number}
              </div>
              <p style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 15,
                fontWeight: 600,
                color: '#1A1A1A',
                margin: '0 0 8px',
                lineHeight: 1.3,
              }}>
                {step.title}
              </p>
              <p style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 14,
                color: '#555555',
                margin: 0,
                lineHeight: 1.6,
              }}>
                {step.body}
              </p>
            </div>
          ))}
        </div>
        <p style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: 13,
          color: '#888888',
          margin: '32px 0 0',
        }}>
          Free to start. You see your job ad before you pay anything.
        </p>
      </div>
      <style>{`
        @media (max-width: 768px) {
          .process-grid {
            grid-template-columns: 1fr 1fr !important;
            gap: 32px !important;
          }
          .process-grid > div {
            padding-right: 0 !important;
            padding-top: 20px !important;
            border-top: 1px solid #E0E0E0 !important;
          }
          .process-grid > div:first-child {
            padding-top: 0 !important;
            border-top: none !important;
          }
        }
        @media (max-width: 480px) {
          .process-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </section>
  );
}

function FeaturesSection() {
  return (
    <section style={{ background: '#F5F5F5', padding: '0 24px 64px' }}>
      <div style={{ maxWidth: 1120, margin: '0 auto' }}>
        <h2 style={{
          fontFamily: 'Inter, sans-serif',
          fontWeight: 600,
          fontSize: 22,
          color: '#1A7A6E',
          margin: '0 0 8px',
          letterSpacing: '-0.3px',
        }}>
          What you get
        </h2>
        <p style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: 14,
          color: '#555555',
          margin: '0 0 32px',
          lineHeight: 1.6,
        }}>
          Four parts, one kit. The interview pack and scorecard are the heart of it.
        </p>

        {/* Hero pair — Interview Pack and Scorecard */}
        <div
          className="hero-cards-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 16,
            marginBottom: 12,
          }}
        >
          {[
            {
              title: 'Interview Pack',
              label: 'Core of the kit',
              desc: 'The questions to ask for that exact role, plus what a strong answer sounds like, what a weak one sounds like, and how to spot the rehearsed one. This is where most interviews fall down. Yours won\'t.',
            },
            {
              title: 'Candidate Scorecard',
              label: 'Core of the kit',
              desc: 'Rate every candidate against the same things, so your decision comes down to evidence instead of who interviewed best on the day or who you happened to click with.',
            },
          ].map((card, i) => (
            <div key={i} style={{
              background: '#FFFFFF',
              border: '2px solid #1A7A6E',
              borderRadius: 8,
              padding: 24,
              boxShadow: '0 2px 8px rgba(26,122,110,0.08)',
            }}>
              <div style={{
                display: 'inline-block',
                background: '#1A7A6E',
                color: '#FFFFFF',
                fontFamily: 'Inter, sans-serif',
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '0.6px',
                textTransform: 'uppercase',
                padding: '3px 8px',
                borderRadius: 4,
                marginBottom: 12,
              }}>
                {card.label}
              </div>
              <h3 style={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                fontSize: 17,
                color: '#1A1A1A',
                margin: '0 0 10px',
                letterSpacing: '-0.2px',
              }}>
                {card.title}
              </h3>
              <p style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 14,
                lineHeight: 1.6,
                color: '#444444',
                margin: 0,
              }}>
                {card.desc}
              </p>
            </div>
          ))}
        </div>

        {/* Supporting pair — Job Ad and Pay Benchmark */}
        <div
          className="support-cards-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 16,
            marginBottom: 20,
          }}
        >
          {[
            {
              title: 'Job Ad',
              desc: 'Written for your role and ready to post to SEEK or Trade Me. Specific enough that the right people apply and the wrong ones don\'t.',
            },
            {
              title: 'Pay Benchmark',
              desc: 'A current median and range for the role in your region, so you can make the offer without guessing or second-guessing.',
            },
          ].map((card, i) => (
            <div key={i} style={{
              background: '#FFFFFF',
              border: '1px solid #E0E0E0',
              borderRadius: 8,
              padding: 20,
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            }}>
              <h3 style={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                fontSize: 15,
                color: '#1A1A1A',
                margin: '0 0 8px',
                letterSpacing: '-0.2px',
                borderBottom: '2px solid #1A7A6E',
                paddingBottom: 8,
              }}>
                {card.title}
              </h3>
              <p style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 13,
                lineHeight: 1.6,
                color: '#555555',
                margin: 0,
              }}>
                {card.desc}
              </p>
            </div>
          ))}
        </div>

        {/* Inline sample — Retail Assistant, two questions + scorecard criterion */}
        <div style={{
          background: '#FAFAFA',
          border: '1px solid #E5E5E5',
          borderRadius: 8,
          padding: 24,
          marginTop: 24,
          marginBottom: 24,
        }}>
          {/* Sample header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 20,
            paddingBottom: 16,
            borderBottom: '1px solid #EEEEEE',
          }}>
            <div>
              <div style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 11,
                fontWeight: 700,
                color: '#888888',
                textTransform: 'uppercase',
                letterSpacing: '0.6px',
                marginBottom: 4,
              }}>
                Example from a real kit
              </div>
              <div style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 13,
                fontWeight: 600,
                color: '#1A1A1A',
              }}>
                Retail Assistant · Auckland
              </div>
            </div>
            <button
              onClick={() => {
                const modal = document.querySelector('[data-sample-modal]');
                if (modal) modal.click();
              }}
              style={{
                background: 'none',
                border: 'none',
                fontFamily: 'Inter, sans-serif',
                fontSize: 13,
                color: '#1A7A6E',
                cursor: 'pointer',
                textDecoration: 'underline',
                padding: 0,
                flexShrink: 0,
              }}
            >
              See full sample kit →
            </button>
          </div>

          {/* Q1 — Role Competency */}
          <div style={{ marginBottom: 20 }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 10,
            }}>
              <span style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 11,
                fontWeight: 700,
                color: '#1A7A6E',
                minWidth: 28,
              }}>Q1</span>
              <span style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 11,
                fontWeight: 600,
                background: '#EAF4F2',
                color: '#1A7A6E',
                borderRadius: 4,
                padding: '2px 7px',
              }}>Role Competency</span>
            </div>
            <p style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: 15,
              fontWeight: 600,
              color: '#1A1A1A',
              lineHeight: 1.4,
              margin: '0 0 10px',
            }}>
              Tell me about a time a customer was unhappy and you had to deal with it on your own. What did you do?
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
              <div style={{ background: '#F5FAF9', borderLeft: '3px solid #1A7A6E', borderRadius: '0 6px 6px 0', padding: '10px 12px' }}>
                <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 10, fontWeight: 700, color: '#1A7A6E', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
                  What a strong answer sounds like
                </div>
                <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: '#333333', lineHeight: 1.6, margin: 0 }}>
                  They describe a real customer and a real complaint, say what they actually did to fix it, and show they stayed calm without needing to get a manager involved. The detail makes it credible.
                </p>
              </div>
              <div style={{ background: '#FFF8EC', borderLeft: '3px solid #B8860B', borderRadius: '0 6px 6px 0', padding: '10px 12px' }}>
                <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 10, fontWeight: 700, color: '#B8860B', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
                  Watch for the rehearsed answer
                </div>
                <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: '#555555', lineHeight: 1.6, margin: 0 }}>
                  A polished answer about staying calm and listening carefully with no actual customer, no actual complaint, and no moment where it was genuinely hard. Real situations have friction in them.
                </p>
              </div>
            </div>

            {/* Scorecard criterion after Q1 */}
            <div style={{
              background: '#FFFFFF',
              border: '1px solid #E0E0E0',
              borderRadius: 6,
              overflow: 'hidden',
              marginTop: 8,
            }}>
              <div style={{
                background: '#EBF5FB',
                borderLeft: '2px solid #1B4F72',
                padding: '7px 12px',
                fontFamily: 'Inter, sans-serif',
                fontSize: 12,
                fontWeight: 700,
                color: '#1B4F72',
              }}>
                Scorecard: Handling customers and complaints
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 1,
                background: '#E0E0E0',
              }}>
                {[
                  { label: 'No (1)', bg: '#FADBDB', color: '#C0392B', text: 'Cannot recall dealing with a complaint without getting a manager.' },
                  { label: 'Maybe (2)', bg: '#FDEBD0', color: '#E67E22', text: 'Describes one example but outcome was unclear or escalated.' },
                  { label: 'Yes (3)', bg: '#D5F5E3', color: '#1A7A6E', text: 'Gives a clear example of resolving a complaint calmly and independently.' },
                  { label: 'Strong Yes (4)', bg: '#D6EAF8', color: '#1B4F72', text: 'Turned a difficult customer into a positive experience and can explain how.' },
                ].map((cell, i) => (
                  <div key={i} style={{ background: cell.bg, padding: '8px 10px' }}>
                    <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 10, fontWeight: 700, color: cell.color, marginBottom: 3 }}>{cell.label}</div>
                    <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 11, color: '#444444', lineHeight: 1.4 }}>{cell.text}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Divider between questions */}
          <div style={{ height: 1, background: '#EEEEEE', margin: '20px 0' }} />

          {/* Q2 — Values and Culture */}
          <div style={{ marginBottom: 16 }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 10,
            }}>
              <span style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 11,
                fontWeight: 700,
                color: '#1A7A6E',
                minWidth: 28,
              }}>Q2</span>
              <span style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 11,
                fontWeight: 600,
                background: '#FFF8EC',
                color: '#B8860B',
                borderRadius: 4,
                padding: '2px 7px',
              }}>Values and Culture</span>
            </div>
            <p style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: 15,
              fontWeight: 600,
              color: '#1A1A1A',
              lineHeight: 1.4,
              margin: '0 0 10px',
            }}>
              Tell me about a time a workmate wasn't pulling their weight. How did you handle it?
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ background: '#F5FAF9', borderLeft: '3px solid #1A7A6E', borderRadius: '0 6px 6px 0', padding: '10px 12px' }}>
                <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 10, fontWeight: 700, color: '#1A7A6E', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
                  What a strong answer sounds like
                </div>
                <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: '#333333', lineHeight: 1.6, margin: 0 }}>
                  They describe keeping their own work up, having a direct word with the person if it kept happening, and not letting it fester or turn into drama. They show they can work alongside people without needing a manager to sort it out.
                </p>
              </div>
              <div style={{ background: '#FFF8EC', borderLeft: '3px solid #B8860B', borderRadius: '0 6px 6px 0', padding: '10px 12px' }}>
                <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 10, fontWeight: 700, color: '#B8860B', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
                  Watch for the rehearsed answer
                </div>
                <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: '#555555', lineHeight: 1.6, margin: 0 }}>
                  A smooth answer about always being a team player and getting on with everyone. The tell: no real person, no real friction, no moment where it was actually uncomfortable. Everyone has worked with someone difficult.
                </p>
              </div>
            </div>
          </div>

          {/* Bottom caption and modal link */}
          <div style={{
            paddingTop: 16,
            borderTop: '1px solid #EEEEEE',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 12,
          }}>
            <p style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: 12,
              color: '#888888',
              margin: 0,
              fontStyle: 'italic',
            }}>
              Every kit includes questions like these, built for your exact role, with coaching notes for each one.
            </p>
            <button
              onClick={() => {
                const modal = document.querySelector('[data-sample-modal]');
                if (modal) modal.click();
              }}
              style={{
                background: 'none',
                border: 'none',
                fontFamily: 'Inter, sans-serif',
                fontSize: 13,
                color: '#1A7A6E',
                cursor: 'pointer',
                textDecoration: 'underline',
                padding: 0,
                flexShrink: 0,
              }}
            >
              See a full sample kit →
            </button>
          </div>
        </div>

        <p style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: 13,
          color: '#777777',
          marginBottom: 0,
          textAlign: 'center',
        }}>
          Delivered as a single PDF, formatted and ready to use. Considers NZ employment law including the Human Rights Act 1993. Not a substitute for legal advice.
        </p>
      </div>
      <style>{`
        @media (max-width: 768px) {
          .hero-cards-grid {
            grid-template-columns: 1fr !important;
          }
          .support-cards-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </section>
  );
}

function NZIdentityBand() {
  return (
    <section style={{
      background: '#1A7A6E',
      padding: '28px 24px',
      textAlign: 'center',
    }}>
      <p style={{
        fontFamily: 'Inter, sans-serif',
        fontSize: 18,
        fontWeight: 600,
        color: '#FFFFFF',
        margin: 0,
        letterSpacing: '-0.2px',
        lineHeight: 1.4,
      }}>
        Made for the way New Zealand businesses actually hire.
      </p>
    </section>
  );
}

function PricingSection() {
  const tiers = [
    {
      name: 'Free',
      price: 'Free',
      desc: 'See your job ad before you pay anything.',
    },
    {
      name: 'One-Time Hiring Kit',
      price: '$29',
      period: '/hire',
      label: 'Most popular',
      priceLead: 'for just',
      desc: 'Full interview kit, salary benchmark, and PDF hiring kit.',
      refund: 'Not useful? Email us within 7 days for a full refund.',
    },
    {
      name: 'Pro Annual',
      price: '$149',
      period: '/yr',
      label: 'Coming soon',
      desc: 'Unlimited hires. All features. Best value for regular hiring.',
      muted: true,
    },
  ];

  return (
    <section style={{ background: '#FFFFFF', padding: '64px 24px' }}>
      <div style={{ maxWidth: 1120, margin: '0 auto' }}>
        <h2 style={{
          fontFamily: 'Inter, sans-serif',
          fontWeight: 600,
          fontSize: 22,
          color: '#1A7A6E',
          margin: '0 0 8px',
          letterSpacing: '-0.3px',
        }}>
          Simple pricing
        </h2>
        <p style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: 16,
          color: '#555555',
          margin: '0 0 32px',
        }}>
          Start free. Pay when you hire.
        </p>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 16,
        }}>
          {tiers.map((t, i) => (
            <div key={i} style={{
              background: t.highlight ? '#EAF4F2' : '#F5F5F5',
              border: t.highlight ? '2px solid #1A7A6E' : t.muted ? '1px solid #EEEEEE' : '1px solid #E5E5E5',
              borderRadius: 8,
              padding: 24,
              opacity: t.muted ? 0.7 : 1,
            }}>
              {t.label && (
                <div style={{
                  display: 'inline-block',
                  background: t.highlight ? '#1A7A6E' : '#555555',
                  color: '#FFFFFF',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                  padding: '3px 8px',
                  borderRadius: 4,
                  marginBottom: 10,
                }}>
                  {t.label}
                </div>
              )}
              <div style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 13,
                fontWeight: 600,
                color: t.muted ? '#AAAAAA' : '#1A7A6E',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: 8,
              }}>
                {t.name}
              </div>
              {t.priceLead && (
                <div style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 11,
                  color: '#555555',
                  marginBottom: 2,
                  letterSpacing: '0.2px',
                }}>
                  {t.priceLead}
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 2, marginBottom: 8 }}>
                <span style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 32,
                  fontWeight: 700,
                  color: t.muted ? '#AAAAAA' : '#1A1A1A',
                }}>
                  {t.price}
                </span>
                {t.period && (
                  <span style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: 14,
                    color: '#555555',
                  }}>
                    {t.period}
                  </span>
                )}
              </div>
              <p style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 14,
                lineHeight: 1.5,
                color: t.muted ? '#AAAAAA' : '#555555',
                margin: 0,
              }}>
                {t.desc}
              </p>
              {t.refund && (
                <p style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 12,
                  color: '#888888',
                  margin: '6px 0 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}>
                  <svg width="10" height="10" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0 }}>
                    <path d="M2 6l3 3 5-5" stroke="#1A7A6E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {t.refund}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer style={{ background: '#1A1A1A', padding: '32px 24px' }}>
      <div style={{
        maxWidth: 1120,
        margin: '0 auto',
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 16,
      }}>
        <span style={{
          fontFamily: 'Inter, sans-serif',
          fontWeight: 700,
          fontSize: 18,
          color: '#1A7A6E',
        }}>
          RoleReady
        </span>
        <span style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: 13,
          color: '#888888',
        }}>
          Built for NZ small business · June 2026 ·{' '}
          <a href="/how-it-works" style={{ color: '#888888', textDecoration: 'none' }}>How It Works</a>
{' · '}
<a href="/interview-questions" style={{ color: '#888888', textDecoration: 'none' }}>Interview Questions</a>
{' · '}
<a href="/sample" style={{ color: '#888888', textDecoration: 'none' }}>Sample Kit</a>
{' · '}
<a href="/pricing" style={{ color: '#888888', textDecoration: 'none' }}>Pricing</a>
{' · '}
          <a href="/faq" style={{ color: '#888888', textDecoration: 'none' }}>FAQ</a>
          {' · '}
          <a href="/privacy-policy" style={{ color: '#888888', textDecoration: 'none' }}>Privacy Policy</a>
          {' · '}
          <a href="/terms-of-service" style={{ color: '#888888', textDecoration: 'none' }}>Terms of Service</a>
          {' · '}
          <a href="/refund-policy" style={{ color: '#888888', textDecoration: 'none' }}>Refund Policy</a>
        </span>
      </div>
    </footer>
  );
}

function LandingScreen({ onCtaClick }) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div style={{ fontFamily: 'Inter, sans-serif' }}>
      <Navbar scrolled={scrolled} onCtaClick={onCtaClick} />
      <HeroSection onCtaClick={onCtaClick} />
      <HooksSection />
      <FeaturesSection />
      <NZIdentityBand />
      <PricingSection />
      <div style={{ background: '#FFFFFF', padding: '24px 24px 48px', textAlign: 'center' }}>
        <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 14, color: '#555555', margin: 0 }}>
          Questions about the process or what's included? <a href="/faq" style={{ color: '#1A7A6E', textDecoration: 'underline' }}>See our FAQ</a>.
        </p>
      </div>
      <Footer />
    </div>
  );
}

const generateRange = (value, type) => {
  if (!value) return '';
  // Parse the FIRST number in the input, not all digits concatenated.
  // A range like "28-30" or "$60,000-70,000" must read as its first figure
  // (28 / 60000), never as "2830" / "6000070000". Commas inside a number are
  // kept; any second number in a typed range is ignored for the preview.
  const cleaned = value.replace(/,/g, '');
  const firstMatch = cleaned.match(/\d+(\.\d+)?/);
  if (!firstMatch) return '';
  const num = parseFloat(firstMatch[0]);
  if (!num) return '';
  if (type === 'salary') {
    const min = Math.round(num * 0.95);
    const max = Math.round(num * 1.08);
    return `$${min.toLocaleString()} – $${max.toLocaleString()}`;
  } else {
    const min = (num * 0.95).toFixed(0);
    const max = (num * 1.1).toFixed(0);
    return `$${min} – $${max}/hr`;
  }
};

const roleSuggestions = [
  'Retail Assistant',
  'Barista',
  'Labourer',
  'Office Administrator',
  'Kitchen Hand',
  'Builder / Carpenter',
  'Chef',
  'Support Worker',
  'Warehouse / Storeperson',
  'Waiter / Front of House',
];

const locationSuggestions = [
  'Kerikeri',
  'Whangārei',
  'North Shore',
  'Auckland',
  'Waitakere',
  'Manukau',
  'Pukekohe',
  'Thames',
  'Hamilton',
  'Cambridge',
  'Te Awamutu',
  'Morrinsville',
  'Matamata',
  'Tauranga',
  'Rotorua',
  'Taupō',
  'Whakatāne',
  'New Plymouth',
  'Hāwera',
  'Whanganui',
  'Napier',
  'Hastings',
  'Gisborne',
  'Palmerston North',
  'Feilding',
  'Levin',
  'Masterton',
  'Kāpiti',
  'Upper Hutt',
  'Lower Hutt',
  'Porirua',
  'Wellington',
  'Nelson',
  'Richmond',
  'Motueka',
  'Blenheim',
  'Westport',
  'Greymouth',
  'Hokitika',
  'Kaikōura',
  'Rangiora',
  'Christchurch',
  'Rolleston',
  'Ashburton',
  'Timaru',
  'Oamaru',
  'Twizel',
  'Wānaka',
  'Queenstown',
  'Cromwell',
  'Alexandra',
  'Dunedin',
  'Balclutha',
  'Gore',
  'Invercargill',
];

function PlatformToggle({ activeView, onSwitch, hasBoth }) {
  if (!hasBoth) return null;
  return (
    <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
      {[
        { value: 'seek', label: 'SEEK NZ' },
        { value: 'trademe', label: 'Trade Me Jobs' },
      ].map(({ value, label }) => (
        <button
          key={value}
          onClick={() => onSwitch(value)}
          style={{
            padding: '6px 14px',
            borderRadius: '20px',
            border: activeView === value ? '2px solid #1A7A6E' : '1px solid #DDDDDD',
            background: activeView === value ? '#E8F3F1' : '#FFFFFF',
            fontFamily: 'Inter, sans-serif',
            fontSize: '13px',
            fontWeight: activeView === value ? 600 : 400,
            color: activeView === value ? '#1A7A6E' : '#555555',
            cursor: 'pointer',
          }}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function GeneratingScreen({ role, location, platform }) {
  const [completedLines, setCompletedLines] = useState(0);

  const platformLabel = platform === 'trademe' ? 'Trade Me Jobs'
    : platform === 'both' ? 'SEEK NZ + Trade Me Jobs'
    : platform === 'notsure' ? 'SEEK NZ'
    : 'SEEK NZ';

  const lines = [
  `Writing your job ad for ${role || 'this role'} in ${location || 'New Zealand'}…`,
  `Finding the current pay range for this role and region…`,
  `Selecting interview questions built for ${role || 'this role'}…`,
  `Adding coaching notes — what strong answers look like, what to watch for…`,
  `Building your candidate scorecard with rating anchors…`,
  `Formatting your job ad for ${platformLabel}…`,
  `Pulling it all together into your hiring kit…`,
];

  useEffect(() => {
    // First step becomes active immediately; each step completes every ~4s
    const lineTimer = setInterval(() => {
      setCompletedLines(v => Math.min(v + 1, lines.length));
    }, 9500);
    return () => clearInterval(lineTimer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Give partial credit for the active (in-progress) step so the bar starts moving immediately
  const progress = Math.min(((completedLines + 0.5) / lines.length) * 100, 95);

  return (
    <div style={{
      minHeight: '100vh',
      background: '#F5F5F5',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 24px',
      fontFamily: 'Inter, sans-serif',
    }}>
      <div style={{ position: 'absolute', top: 24, left: 24, fontWeight: 700, fontSize: 20, color: '#1A7A6E' }}>
        RoleReady
      </div>

      <div style={{ maxWidth: 480, width: '100%' }}>
        <h2 style={{ fontSize: 22, fontWeight: 600, color: '#1A1A1A', marginBottom: 8, letterSpacing: '-0.3px' }}>
          Building your hiring kit…
        </h2>
        <p style={{ fontSize: 14, color: '#555555', marginBottom: 32, lineHeight: 1.5 }}>
          This usually takes 30–90 seconds. We're generating your job ad, interview questions, scorecard, and pay benchmark, worth the wait.
        </p>

        <div style={{ height: 3, background: '#E0E0E0', borderRadius: 2, marginBottom: 32, overflow: 'hidden' }}>
          <div style={{
            height: '100%',
            width: `${progress}%`,
            background: '#1A7A6E',
            borderRadius: 2,
            transition: 'width 0.8s ease',
          }} />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 40 }}>
          {lines.map((line, i) => {
            const isDone    = i < completedLines;
            const isActive  = i === completedLines;
            const isPending = i > completedLines;
            return (
              <div key={i} style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                opacity: isPending ? 0.2 : 1,
                transition: 'opacity 0.5s ease',
              }}>
                <div style={{
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  background: isDone ? '#1A7A6E' : 'transparent',
                  border: isDone ? 'none' : isActive ? '2px solid #1A7A6E' : '2px solid #CCCCCC',
                  flexShrink: 0,
                  marginTop: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.4s ease, border-color 0.4s ease',
                }}>
                  {isDone && (
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <path d="M2 5l2.5 2.5 3.5-4" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                  {isActive && (
                    <div className="step-pulse" style={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      background: '#1A7A6E',
                    }} />
                  )}
                </div>
                <span style={{
                  fontSize: 14,
                  color: isPending ? '#AAAAAA' : '#1A1A1A',
                  lineHeight: 1.5,
                  transition: 'color 0.4s ease',
                }}>
                  {line}
                </span>
              </div>
            );
          })}
        </div>

      </div>
    </div>
  );
}

function GeneratorScreen({
  isUnlocked = false,
  onEmailContinue,
  onGenerationComplete,
  initialRole = '',
  initialLocation = '',
  initialJobAd = '',
  initialQuestions = [],
  initialSalary = null,
  initialInterviewPack = null,
  initialJobAdData = null,
}) {
  const [role, setRole] = useState(initialRole || '');
  const [location, setLocation] = useState(initialLocation || 'Auckland');
  const [employmentType, setEmploymentType] = useState('Full-time');
  const [salary, setSalary] = useState('');
  const [company, setCompany] = useState('');
  const [businessContext, setBusinessContext] = useState('');
  const [roleStarted, setRoleStarted] = useState(initialRole ? true : false);
  const [payType, setPayType] = useState('salary');
  const [showRoleSuggestions, setShowRoleSuggestions] = useState(false);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const [generated, setGenerated] = useState(isUnlocked);
  const [email, setEmail] = useState('');
  const [hours, setHours] = useState('');
  const [platform, setPlatform] = useState('seek');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState('');
  const [aiJobAd, setAiJobAd] = useState(initialJobAd || '');
  const [aiJobAdData, setAiJobAdData] = useState(initialJobAdData || null);
  const [aiInterviewQuestions, setAiInterviewQuestions] = useState(initialQuestions || []);
  const [aiInterviewPack, setAiInterviewPack] = useState(initialInterviewPack || null);
  const [attemptId, setAttemptId] = useState(null);
  const [adPlatformView, setAdPlatformView] = useState('seek');

  useEffect(() => {
    setAdPlatformView(platform === 'trademe' ? 'trademe' : 'seek');
  }, [platform]);

  const MIN_WAGE = 23.15;

  const copyButtonStyle = {
    marginTop: '12px',
    padding: '10px 14px',
    borderRadius: '6px',
    border: '1px solid #CCCCCC',
    background: '#fff',
    cursor: 'pointer',
    fontFamily: 'Inter, sans-serif',
    fontSize: '14px',
  };

  const labelStyle = {
    display: 'block',
    fontSize: '14px',
    fontWeight: 600,
    marginTop: '16px',
    color: '#1A1A1A',
  };

  const inputStyle = {
    width: '100%',
    padding: '14px',
    borderRadius: '8px',
    border: '1px solid #DDD',
    marginTop: '6px',
    fontSize: '15px',
    boxSizing: 'border-box',
    fontFamily: 'Inter, sans-serif',
  };

  const getErrorMessage = (error) => {
    const msg = error?.message?.toLowerCase() ?? '';
    if (
      msg.includes('load failed') ||
      msg.includes('network') ||
      msg.includes('abort') ||
      msg.includes('failed to fetch')
    ) {
      return 'Generation interrupted. This can happen if your phone locked or you switched apps. Tap to try again.';
    }
    return 'Generation failed. Please try again.';
  };

  const handleGenerate = async () => {
    const cleanRole = role?.trim() || '';
    if (!cleanRole) {
      alert('Please enter a role');
      return;
    }
    setGenerateError('');
    setIsGenerating(true);
    window.scrollTo(0, 0);
    let currentAttemptId = null;
    // Only clear kit state on a fresh generation — not when a paid user regenerates
    if (!isUnlocked) {
      localStorage.removeItem('roleready_kit_state');
    }

    // Non-blocking generation attempt record — failure must never block kit generation
    try {
      const { data: gaData, error: gaError } = await supabase
        .from('generation_attempts')
        .insert({
          role: cleanRole,
          location: location || null,
          platform: platform || null,
          employment_type: employmentType || null,
          salary_provided: !!salary,
          salary_amount: salary ? parseFloat(salary.replace(/[^0-9.]/g, '')) || null : null,
          completed: false,
          email_captured: false,
          paid: false,
          market: 'NZ',
          company_name: company?.trim() || null,
          business_context: businessContext?.trim() || null,
        })
        .select('id')
        .single();
        if (gaError) {
        console.error('SUPABASE GENERATION ATTEMPT ERROR:', JSON.stringify(gaError));
      } else if (gaData?.id) {
        currentAttemptId = gaData.id;
        setAttemptId(gaData.id);
      }
    } catch (dbErr) {
      console.error('SUPABASE GENERATION ATTEMPT EXCEPTION:', dbErr.message);
    }

    try {
      const response = await fetch('/api/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          role: cleanRole,
          location,
          employmentType,
          hours,
          salary,
          payType,
          company,
          businessContext,
          platform,
          libraryQuestions: (() => {
            const slug = toRoleSlug(cleanRole);
            const qs = getQuestionsForRole(slug);
            return isDefaultQuestions(qs) ? null : qs;
          })(),
        }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `Server error ${response.status}`);
      }
      const data = await response.json();
      setAiJobAd(data.jobAd || '');
      setAiJobAdData(data);
      setAiInterviewQuestions(data.interviewQuestions || []);
      setAiInterviewPack(data.interviewPack || null);
      setGenerated(true);
      track('job_ad_generated', { role: cleanRole, location });
            // Non-blocking output capture for quality auditing — failure must never affect the kit
      if (currentAttemptId) {
        try {
          await supabase
            .from('generation_attempts')
            .update({
              completed: true,
              output_json: {
                jobAd: data.jobAd || null,
                seekAd: data.seekAd || null,
                tradeMeAd: data.tradeMeAd || null,
                interviewPack: data.interviewPack || null,
                scorecardCriteria: data.scorecardCriteria || null,
                platform: data.platform || null,
              },
            })
            .eq('id', currentAttemptId);
        } catch (storeErr) {
          console.error('Output capture failed (non-blocking):', storeErr);
        }
      }
      if (onGenerationComplete) {
        onGenerationComplete(
          cleanRole,
          location,
          data.jobAd || '',
          data.interviewQuestions || [],
          null,
          data.interviewPack || null,
          data,
        );
      }
    } catch (err) {
      console.error('Generation error:', err);
      setGenerateError(getErrorMessage(err));
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <>
      <div style={{ maxWidth: '720px', margin: '0 auto', padding: '24px' }}>

      {/* INPUT FORM */}
      {!generated && (
        isGenerating ? (
          <GeneratingScreen role={role} location={location} platform={platform} />
        ) : (
        <div>

          <h2 style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '28px',
            fontWeight: 700,
            letterSpacing: '-0.4px',
            color: '#1A1A1A',
            marginBottom: '12px',
          }}>
            Generate your hiring kit
          </h2>

          {/* ROLE INPUT */}
          <input
            type="text"
            placeholder="e.g. Cafe Manager, Retail Assistant, Builder"
            value={role}
            onChange={(e) => {
              setRole(e.target.value);
              setShowRoleSuggestions(true);
              if (e.target.value.length > 2) {
                setRoleStarted(true);
              }
            }}
            style={{
              ...inputStyle,
              padding: '16px',
              fontSize: '17px',
              marginTop: '16px',
              marginBottom: '12px',
            }}
          />

          {/* ROLE SUGGESTIONS */}
          {showRoleSuggestions && role.length > 0 && (
            <div style={{
              position: 'relative',
              zIndex: 10,
              background: '#fff',
              border: '1px solid #DDD',
              borderRadius: '6px',
              marginBottom: '8px',
              overflow: 'hidden',
            }}>
              {roleSuggestions
                .filter((r) => r.toLowerCase().includes(role.toLowerCase()))
                .slice(0, 5)
                .map((r) => (
                  <div
                    key={r}
                    onMouseDown={() => {
                      setRole(r);
                      setRoleStarted(true);
                      setShowRoleSuggestions(false);
                    }}
                    style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #EEE', fontFamily: 'Inter, sans-serif', fontSize: '14px' }}
                  >
                    {r}
                  </div>
                ))}
            </div>
          )}

          {/* EMPTY STATE HINT */}
          {!roleStarted && roleSuggestions.filter((r) => role.length > 0 && r.toLowerCase().includes(role.toLowerCase())).length === 0 && (
            <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#999', marginTop: '4px', marginBottom: '12px' }}>
              Start by entering the role you're hiring for
            </p>
          )}

          {/* QUICK-SELECT CHIPS */}
          {role.length === 0 && (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '4px' }}>
              {roleSuggestions.slice(0, 10).map((r) => (
                <button
                  key={r}
                  onClick={() => {
                    setRole(r);
                    setRoleStarted(true);
                  }}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '20px',
                    border: '1px solid #DDD',
                    background: '#fff',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '13px',
                    cursor: 'pointer',
                  }}
                >
                  {r}
                </button>
              ))}
            </div>
          )}

          {/* OPTIONAL INPUTS */}
          {roleStarted && (
            <div style={{
              marginTop: '20px',
              padding: '16px',
              background: '#FAFAFA',
              border: '1px solid #EAEAEA',
              borderRadius: '10px',
            }}>

              <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', fontWeight: 600, color: '#555', marginBottom: '2px' }}>
                Optional - improve your results
              </p>

              {/* LOCATION */}
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Location</label>
                <input
                  type="text"
                  value={location}
                  onFocus={() => {
                    if (location === 'Auckland') setLocation('');
                  }}
                  onChange={(e) => {
                    setLocation(e.target.value);
                    setShowLocationSuggestions(true);
                  }}
                  placeholder="e.g. Auckland"
                  style={inputStyle}
                />
                {showLocationSuggestions && location.length > 0 && (
                  <div style={{
                    position: 'relative',
                    zIndex: 10,
                    background: '#fff',
                    border: '1px solid #DDD',
                    borderRadius: '6px',
                    marginTop: '4px',
                    overflow: 'hidden',
                  }}>
                    {locationSuggestions
                      .filter((l) => l.toLowerCase().includes(location.toLowerCase()))
                      .slice(0, 5)
                      .map((l) => (
                        <div
                          key={l}
                          onMouseDown={() => {
                            setLocation(l);
                            setShowLocationSuggestions(false);
                          }}
                          style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #EEE', fontFamily: 'Inter, sans-serif', fontSize: '14px' }}
                        >
                          {l}
                        </div>
                      ))}
                  </div>
                )}
              </div>

              {/* EMPLOYMENT TYPE */}
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Employment Type</label>
                <div style={{ marginTop: '6px' }}>
                  <select
                    value={employmentType}
                    onChange={(e) => setEmploymentType(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '14px',
                      borderRadius: '8px',
                      border: '1px solid #DDD',
                      fontSize: '15px',
                      background: '#fff',
                      fontFamily: 'Inter, sans-serif',
                    }}
                  >
                    <option>Full-time</option>
                    <option>Part-time</option>
                    <option>Casual</option>
                  </select>
                </div>
                {(employmentType === 'Part-time' || employmentType === 'Casual') && (
                  <div style={{ marginTop: '12px' }}>
                    <label style={labelStyle}>Hours per week (approx)</label>
                    <input
                      type="number"
                      value={hours}
                      onChange={(e) => setHours(e.target.value)}
                      placeholder="e.g. 20"
                      style={inputStyle}
                    />
                    <div style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: '#777', marginTop: '4px' }}>
                      Helps us format your job ad correctly
                    </div>
                  </div>
                )}
              </div>

              {/* PLATFORM */}
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Where are you posting?</label>
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
                  {[
                    { value: 'seek', label: 'SEEK NZ' },
                    { value: 'trademe', label: 'Trade Me Jobs' },
                    { value: 'both', label: 'Both' },
                    { value: 'notsure', label: 'Not sure' },
                  ].map(({ value, label }) => (
                    <button
                      key={value}
                      onClick={() => setPlatform(value)}
                      style={{
                        padding: '8px 12px',
                        borderRadius: '20px',
                        border: platform === value ? '2px solid #1A7A6E' : '1px solid #DDD',
                        background: platform === value ? '#E8F3F1' : '#fff',
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '13px',
                        cursor: 'pointer',
                        fontWeight: platform === value ? 600 : 400,
                        color: platform === value ? '#1A7A6E' : '#333',
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* PAY */}
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Pay (optional, we'll suggest a range)</label>
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                  <button
                    onClick={() => setPayType('salary')}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '20px',
                      border: payType === 'salary' ? '2px solid #1A7A6E' : '1px solid #DDD',
                      background: payType === 'salary' ? '#E8F3F1' : '#fff',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '13px',
                      cursor: 'pointer',
                    }}
                  >
                    Salary
                  </button>
                  <button
                    onClick={() => setPayType('hourly')}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '20px',
                      border: payType === 'hourly' ? '2px solid #1A7A6E' : '1px solid #DDD',
                      background: payType === 'hourly' ? '#E8F3F1' : '#fff',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '13px',
                      cursor: 'pointer',
                    }}
                  >
                    Hourly rate
                  </button>
                </div>
                <input
                  type="text"
                  value={salary}
                  onChange={(e) => setSalary(e.target.value)}
                  placeholder={payType === 'salary' ? '$60,000' : '$25/hour'}
                  style={inputStyle}
                />
                {salary && (
                  <div style={{ fontFamily: 'Inter, sans-serif', marginTop: '6px', fontSize: '14px', color: '#1A7A6E' }}>
                    Suggested range: {generateRange(salary, payType)}
                  </div>
                )}
                {payType === 'hourly' && salary && parseFloat(salary.replace(/[^0-9.]/g, '')) < MIN_WAGE && (
                  <div style={{ fontFamily: 'Inter, sans-serif', marginTop: '6px', fontSize: '13px', color: '#B00020' }}>
                    Below NZ minimum wage. Check before publishing
                  </div>
                )}
                <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: '#777', marginTop: '4px' }}>
                  {salary ? 'Roles with clear pay attract more applicants' : 'Optional, the pay line will be omitted if left blank'}
                </p>
              </div>

              {/* COMPANY */}
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Company (optional)</label>
                <input
                  type="text"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder="e.g. Wilson Builders"
                  style={inputStyle}
                />
              </div>

              {/* BUSINESS CONTEXT */}
              <div style={{ marginBottom: '4px' }}>
                <label style={labelStyle}>About your business (optional, but it makes your job ad noticeably better)</label>
                <textarea
                  value={businessContext}
                  onChange={(e) => setBusinessContext(e.target.value)}
                  placeholder="The more you tell us, the sharper your ad. Useful things to include: how big your team is, the type of work (residential, commercial, fast-paced, specialist), and what makes this role or workplace stand out."
                  rows={3}
                  style={{
                    ...inputStyle,
                    resize: 'vertical',
                    minHeight: '80px',
                  }}
                />
                <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: '#777', marginTop: '4px' }}>
                  Used to personalise your job ad, not quoted verbatim. We'll use this to write a more specific, relevant ad.
                </p>
              </div>

            </div>
          )}

          {generateError && (
            <div style={{
              marginTop: '12px',
              padding: '12px',
              background: '#FFF0F0',
              border: '1px solid #FFCCCC',
              borderRadius: '8px',
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              color: '#B00020',
            }}>
              {generateError}
              <button
                onClick={handleGenerate}
                className="mt-3 w-full py-2 px-4 bg-teal-600 text-white rounded-lg font-medium text-sm"
                style={{
                  marginTop: '12px',
                  width: '100%',
                  padding: '8px 16px',
                  background: '#0D9488',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 500,
                  fontSize: '14px',
                  cursor: 'pointer',
                }}>
                Try again
              </button>
            </div>
          )}

          <button
            onClick={handleGenerate}
            disabled={isGenerating}
            style={{
              marginTop: '24px',
              background: isGenerating ? '#999' : '#B8860B',
              color: '#fff',
              border: 'none',
              padding: '14px',
              borderRadius: '8px',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              fontSize: '16px',
              width: '100%',
              cursor: isGenerating ? 'not-allowed' : 'pointer',
            }}
          >
            {isGenerating ? 'Generating your hiring kit…' : 'Continue →'}
          </button>

        </div>
        )
      )}

      {/* OUTPUT SCREEN */}
      {generated && (
        <div style={{ maxWidth: '720px', margin: '0 auto', padding: '24px' }}>

          <h2 style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '28px',
            fontWeight: 700,
            letterSpacing: '-0.4px',
            color: '#1A1A1A',
            marginBottom: '16px',
          }}>
            Your hiring kit for a {role.trim()} in {location || 'your area'}
          </h2>

          {/* FRAMING LINE */}
          {!isUnlocked && (
            <div style={{
              background: '#EBEBEB',
              borderRadius: '8px',
              padding: '12px 16px',
              marginTop: '16px',
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              color: '#444444',
              lineHeight: 1.5,
            }}>
              This is your free job ad. The interview pack and scorecard below are what stop you making the wrong hire.
            </div>
          )}

          {/* PDF DOWNLOAD — only shown when unlocked */}
          {isUnlocked && (
            <button
              onClick={() => {
                const slugForCriteria = toRoleSlug(role.trim());
                const criteria = getCriteriaForRole(slugForCriteria);
                const dbResult = lookupSalary(role.trim(), location);
                generateHiringKitPDF({
                  role: role.trim(),
                  location: location,
                  businessName: company || '',
                  jobAd: aiJobAd,
                  jobAdData: aiJobAdData,
                  platform: aiJobAdData?.platform || platform,
                  scorecardCriteria: criteria,
                  apiScorecardCriteria: aiJobAdData?.scorecardCriteria ?? null,
                  screeningCriteria: aiJobAdData?.screeningCriteria ?? null,
                  interviewPack: aiInterviewPack,
                  salaryData: dbResult.found ? dbResult : null,
                });
              }}
              style={{
                width: '100%',
                padding: '16px',
                background: '#B8860B',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                marginTop: '16px',
                fontFamily: 'Inter, sans-serif',
              }}
            >
              Download full hiring kit as PDF →
            </button>
          )}

          {/* HOW TO USE THIS KIT — orientation strip, unlocked only */}
          {isUnlocked && (
            <div style={{ background: '#F5F5F5', borderRadius: '8px', padding: '14px 16px', marginTop: '16px', fontFamily: 'Inter, sans-serif' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#888', letterSpacing: '0.6px', textTransform: 'uppercase', marginBottom: '10px' }}>How to use this kit</div>
              <ol style={{ margin: 0, padding: '0 0 0 18px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <li style={{ fontSize: '13px', color: '#444', lineHeight: 1.5 }}><strong>Post your job ad</strong> — copy it straight to SEEK or Trade Me using the buttons below.</li>
                <li style={{ fontSize: '13px', color: '#444', lineHeight: 1.5 }}><strong>Run the interview</strong> — work through the interview pack question by question.</li>
                <li style={{ fontSize: '13px', color: '#444', lineHeight: 1.5 }}><strong>Score every candidate</strong> — use the scorecard for each person, same criteria every time.</li>
                <li style={{ fontSize: '13px', color: '#444', lineHeight: 1.5 }}><strong>Make your offer</strong> — compare your scorecards, then decide. The pay range below is a useful reference if you're unsure what to offer.</li>
              </ol>
            </div>
          )}

          {/* 1. JOB AD */}
          {(() => {
            const stripMd = (text) => (text || '')
              .replace(/#{1,6}\s+/g, '')
              .replace(/\*\*(.*?)\*\*/g, '$1')
              .replace(/\*(.*?)\*/g, '$1')
              .replace(/^-\s/gm, '• ')
              .trim();

            const fieldLabel = { fontFamily: 'Inter, sans-serif', fontSize: '11px', fontWeight: 700, letterSpacing: '0.6px', textTransform: 'uppercase', color: '#888', marginBottom: '4px', marginTop: '16px' };
            const fieldBody = { fontFamily: 'Inter, sans-serif', fontSize: '14px', lineHeight: 1.65, color: '#222', whiteSpace: 'pre-wrap', marginBottom: '2px' };
            const cardWrap = { background: '#fff', border: '1px solid #E5E5E5', borderRadius: '10px', padding: '20px', marginTop: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' };

            const CopyBtn = ({ text, label = 'Copy' }) => (
              <button
                style={{ ...copyButtonStyle, marginTop: '6px', fontSize: '12px', padding: '6px 10px' }}
                onClick={() => navigator.clipboard.writeText(text || '')}
              >
                {label}
              </button>
            );

            const BulletList = ({ items }) => (
              <div>
                {(items || []).map((b, i) => (
                  <p key={i} style={{ ...fieldBody, margin: '2px 0', paddingLeft: '4px' }}>• {stripMd(b)}</p>
                ))}
              </div>
            );

            const seekAd = aiJobAdData && aiJobAdData.seekAd;
            const tradeMeAd = aiJobAdData && aiJobAdData.tradeMeAd;
            const hasStructuredData = !!(seekAd || tradeMeAd);
            const hasBothAds = !!(seekAd && tradeMeAd);

            const showSeek = adPlatformView === 'seek' || (!tradeMeAd && seekAd);
            const showTrademe = adPlatformView === 'trademe' || (!seekAd && tradeMeAd);

            const SeekContent = () => seekAd ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                  <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', fontWeight: 600, color: '#1A7A6E' }}>SEEK NZ - paste each field below</span>
                </div>
                <div style={{ height: 1, background: '#E5E5E5', marginBottom: '4px' }} />

                <div style={fieldLabel}>Job Title</div>
                <p style={fieldBody}>{stripMd(seekAd.jobTitle)}</p>
                {isUnlocked && <CopyBtn text={seekAd.jobTitle} />}

                {seekAd.highlights && seekAd.highlights.length > 0 && (
                  <>
                    <div style={fieldLabel}>Search Result Highlights</div>
                    <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: '#777777', marginBottom: '6px', fontStyle: 'italic' }}>
                      These 3 points appear in SEEK search results before candidates click your ad.
                    </p>
                    {seekAd.highlights.map((h, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '4px' }}>
                        <span style={{ color: '#1A7A6E', fontWeight: 700, flexShrink: 0 }}>✓</span>
                        <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#222', margin: 0 }}>{h}</p>
                      </div>
                    ))}
                    {isUnlocked && <CopyBtn text={(seekAd.highlights || []).map(h => `• ${h}`).join('\n')} label="Copy highlights" />}
                  </>
                )}

                <div style={fieldLabel}>About the Role</div>
                <p style={fieldBody}>{stripMd(seekAd.aboutTheRole)}</p>
                {isUnlocked && <CopyBtn text={seekAd.aboutTheRole} />}

                <div style={fieldLabel}>What You'll Do</div>
                <BulletList items={seekAd.whatYoullDo} />
                {isUnlocked && <CopyBtn text={(seekAd.whatYoullDo || []).map(b => `• ${b}`).join('\n')} />}

                <div style={fieldLabel}>What We're Looking For</div>
                <BulletList items={seekAd.whatWereLookingFor} />
                {isUnlocked && <CopyBtn text={(seekAd.whatWereLookingFor || []).map(b => `• ${b}`).join('\n')} />}

                {seekAd.whatWeOffer && seekAd.whatWeOffer.length > 0 && (
                  <>
                    <div style={fieldLabel}>What We Offer</div>
                    <BulletList items={seekAd.whatWeOffer} />
                    {isUnlocked && <CopyBtn text={(seekAd.whatWeOffer || []).map(b => `• ${b}`).join('\n')} />}
                  </>
                )}

                <div style={fieldLabel}>About Us</div>
                <p style={fieldBody}>{stripMd(seekAd.aboutUs)}</p>
                {isUnlocked && <CopyBtn text={seekAd.aboutUs} />}

                <div style={fieldLabel}>Application</div>
                <p style={fieldBody}>{stripMd(seekAd.applicationTrigger)}</p>
                {isUnlocked && <CopyBtn text={seekAd.applicationTrigger} />}

                {isUnlocked && (
                  <div style={{ marginTop: '16px' }}>
                    <CopyBtn text={[seekAd.jobTitle, seekAd.highlights ? seekAd.highlights.map(h => `• ${h}`).join('\n') : null, seekAd.aboutTheRole, (seekAd.whatYoullDo || []).map(b => `• ${b}`).join('\n'), (seekAd.whatWereLookingFor || []).map(b => `• ${b}`).join('\n'), seekAd.whatWeOffer ? (seekAd.whatWeOffer || []).map(b => `• ${b}`).join('\n') : null, seekAd.aboutUs, seekAd.applicationTrigger].filter(Boolean).join('\n\n')} label="Copy full SEEK ad" />
                  </div>
                )}
                {isUnlocked && aiJobAdData?.seekNote && (
                  <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: '#888', marginTop: '8px', fontStyle: 'italic' }}>{aiJobAdData.seekNote}</p>
                )}
              </>
            ) : null;

            const TradeMeContent = () => tradeMeAd ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                  <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', fontWeight: 600, color: '#1A7A6E' }}>Trade Me Jobs - paste below</span>
                </div>
                <div style={{ height: 1, background: '#E5E5E5', marginBottom: '4px' }} />
                <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: '#777777', marginBottom: '8px', fontStyle: 'italic' }}>
                  Trade Me shows the first 2 lines in search results. Your hook is already in the opening.
                </p>

                <div style={fieldLabel}>Job Title</div>
                <p style={fieldBody}>{stripMd(tradeMeAd.jobTitle)}</p>
                {isUnlocked && <CopyBtn text={tradeMeAd.jobTitle} />}

                <div style={fieldLabel}>Job Description</div>
                <p style={fieldBody}>{stripMd(tradeMeAd.jobDescription)}</p>
                {isUnlocked && <CopyBtn text={tradeMeAd.jobDescription} label="Copy full listing" />}
              </>
            ) : null;

            const FallbackContent = () => (
              <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', lineHeight: 1.65, color: '#333', whiteSpace: 'pre-wrap' }}>
                {stripMd(aiJobAd)}
                {isUnlocked && <CopyBtn text={aiJobAd} label="Copy job ad" />}
              </p>
            );

            const platformDestLabel = hasBothAds ? 'SEEK NZ or Trade Me Jobs' : seekAd ? 'SEEK NZ' : 'Trade Me Jobs';

            return (
              <div style={cardWrap}>
                <h3 style={{ fontFamily: 'Inter, sans-serif', fontSize: '16px', fontWeight: 700, borderBottom: '2px solid #1A7A6E', paddingBottom: '6px', marginBottom: '12px', color: '#1A1A1A' }}>
                  Job Ad
                </h3>

                <PlatformToggle activeView={adPlatformView} onSwitch={setAdPlatformView} hasBoth={hasBothAds} />

                {hasStructuredData ? (
                  <>
                    {showSeek && <SeekContent />}
                    {showTrademe && !showSeek && <TradeMeContent />}
                  </>
                ) : (
                  <FallbackContent />
                )}

                {!isUnlocked && hasStructuredData && (
                  <div style={{ marginTop: '16px', padding: '12px 16px', background: '#F5F5F5', borderRadius: '6px', border: '1px solid #E0E0E0', fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#555555', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ color: '#1A7A6E' }}>🔒</span>
                    Your job ad is ready to post. Unlock below to copy it directly into {platformDestLabel}.
                  </div>
                )}
              </div>
            );
          })()}

          {/* 2. CANDIDATE SCORECARD — hero artefact, immediately after Job Ad */}
          <ScorecardCard
            role={role.trim()}
            isUnlocked={isUnlocked}
            apiScorecardCriteria={aiJobAdData?.scorecardCriteria ?? null}
          />

          {/* 3. INTERVIEW PACK */}
          {(() => {
            const pack = aiInterviewPack;
            const questions = pack && pack.questions ? pack.questions : [];

            // Flat question list for preview fallback
            const flatQuestions = Array.isArray(aiInterviewQuestions) && aiInterviewQuestions.length > 0
              ? aiInterviewQuestions
              : typeof aiInterviewQuestions === 'string'
                ? aiInterviewQuestions.split('\n').filter(l => l.trim()).map(l => l.replace(/^\d+\.\s*/, ''))
                : [];

            // eslint-disable-next-line no-unused-vars
            const PREVIEW_COUNT = 3;

            const categoryBadge = (cat) => {
              const colours = {
                'Role Competency': { bg: '#EAF4F2', text: '#1A7A6E' },
                'Values & Culture': { bg: '#FFF8EC', text: '#B8860B' },
                'Practical': { bg: '#F0F0F0', text: '#555' },
              };
              const c = colours[cat] || colours['Practical'];
              return (
                <span style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '11px',
                  fontWeight: 600,
                  background: c.bg,
                  color: c.text,
                  borderRadius: '4px',
                  padding: '2px 7px',
                  letterSpacing: '0.2px',
                }}>
                  {cat}
                </span>
              );
            };

            const QuestionCard = ({ q, index, showCoaching }) => {
              const [weakExpanded, setWeakExpanded] = useState(false);
              return (
                <div style={{
                  borderTop: '1px solid #F0F0F0',
                  paddingTop: '16px',
                  marginTop: '16px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', fontWeight: 700, color: '#1A7A6E', minWidth: '28px' }}>
                      Q{q.number || index + 1}
                    </span>
                    {q.category && categoryBadge(q.category)}
                  </div>
                  <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', fontWeight: 600, color: '#1A1A1A', lineHeight: 1.5, margin: '0 0 10px' }}>
                    {q.question}
                  </p>
                  {showCoaching && (
                    <>
                      {q.strongAnswer && (
                        <div style={{ background: '#F5FAF9', borderLeft: '3px solid #1A7A6E', padding: '10px 12px', borderRadius: '0 6px 6px 0', marginBottom: '8px' }}>
                          <div style={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', fontWeight: 700, color: '#1A7A6E', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                            What a strong answer sounds like
                          </div>
                          <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#333', lineHeight: 1.6, margin: 0 }}>{q.strongAnswer}</p>
                        </div>
                      )}
                      {q.watchFor && (
                        <div style={{ background: '#FFF8EC', borderLeft: '3px solid #B8860B', padding: '10px 12px', borderRadius: '0 6px 6px 0', marginBottom: '8px' }}>
                          <div style={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', fontWeight: 700, color: '#B8860B', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                            Watch for the rehearsed answer
                          </div>
                          <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#555', lineHeight: 1.6, margin: 0 }}>{q.watchFor}</p>
                        </div>
                      )}
                      {q.weakAnswer && (
                        <div>
                          <button
                            onClick={() => setWeakExpanded(e => !e)}
                            style={{
                              background: 'none',
                              border: 'none',
                              fontFamily: 'Inter, sans-serif',
                              fontSize: '12px',
                              color: '#888',
                              cursor: 'pointer',
                              padding: '0',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                            }}
                          >
                            <span style={{ transform: weakExpanded ? 'rotate(180deg)' : 'none', display: 'inline-block', transition: 'transform 0.15s' }}>▾</span>
                            {weakExpanded ? 'Hide' : 'What a weak answer looks like'}
                          </button>
                          {weakExpanded && (
                            <div style={{ background: '#FFF5F5', borderLeft: '3px solid #CC4444', padding: '10px 12px', borderRadius: '0 6px 6px 0', marginTop: '6px' }}>
                              <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#555', lineHeight: 1.6, margin: 0 }}>{q.weakAnswer}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            };

            const packCopyText = pack
              ? [
                  `Interview Pack - ${role.trim()}`,
                  '',
                  pack.openingNote || '',
                  '',
                  `Character pressure for this hire: "${pack.characterPressure || ''}"`,
                  '',
                  ...(questions.flatMap((q, i) => [
                    `Q${q.number || i + 1} [${q.category}]`,
                    q.question,
                    '',
                    `Strong answer: ${q.strongAnswer}`,
                    `Watch for: ${q.watchFor}`,
                    `Weak answer: ${q.weakAnswer}`,
                    '',
                  ])),
                  'NZ Compliance Note',
                  pack.complianceNote || '',
                  '',
                  pack.creditLine || '',
                ].join('\n')
              : flatQuestions.map((q, i) => `${i + 1}. ${q}`).join('\n');

            return (
              <div style={{ background: '#fff', border: '1px solid #E5E5E5', borderRadius: '10px', padding: '20px', marginTop: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                <h3 style={{ fontFamily: 'Inter, sans-serif', fontSize: '16px', fontWeight: 700, borderBottom: '2px solid #1A7A6E', paddingBottom: '6px', marginBottom: '8px', color: '#1A1A1A' }}>
                  Interview Pack
                </h3>
                <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#6B7280', marginBottom: '16px', fontWeight: 400 }}>
                  Your question-by-question guide for the interview itself, includes what a strong answer sounds like and what to watch for.
                </p>

                {!isUnlocked ? (
                  <>
                    {/* Role-aware teaser — shows first question from role's actual library */}
                    {(() => {
                      const teaserSlug = toRoleSlug(role.trim());
                      const teaserQuestions = getQuestionsForRole(teaserSlug);
                      const isLibraryRole = !isDefaultQuestions(teaserQuestions);
                      const teaserQ1 = isLibraryRole ? teaserQuestions[0] : DEFAULT_QUESTIONS[1];
        const teaserCoaching = isLibraryRole
          ? 'Listen for a specific situation with real detail, what they actually did, and what happened. Vague or rehearsed answers skate over all three.'
          : 'Listen for a real situation, the actions they personally took, and the outcome. A polished answer with no specifics is the thing to watch for.';
                      return (
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', fontWeight: 700, color: '#1A7A6E', minWidth: '28px' }}>Q1</span>
                        <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', fontWeight: 600, background: '#EAF4F2', color: '#1A7A6E', borderRadius: '4px', padding: '2px 7px' }}>Role Competency</span>
                      </div>
                      <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', fontWeight: 600, color: '#1A1A1A', lineHeight: 1.5, margin: '0 0 10px' }}>
                        {teaserQ1}
                      </p>
                      <div style={{ background: '#F5FAF9', borderLeft: '3px solid #1A7A6E', padding: '10px 12px', borderRadius: '0 6px 6px 0', marginBottom: '10px' }}>
                        <div style={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', fontWeight: 700, color: '#1A7A6E', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                          What a strong answer sounds like
                        </div>
                        <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#333', lineHeight: 1.6, margin: 0 }}>
                          {teaserCoaching}
                        </p>
                      </div>
                      <div style={{ background: '#FFF8EC', borderLeft: '3px solid #B8860B', padding: '10px 12px', borderRadius: '0 6px 6px 0' }}>
                        <div style={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', fontWeight: 700, color: '#B8860B', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                          Watch for the rehearsed answer
                        </div>
                        <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#555', lineHeight: 1.6, margin: 0 }}>
                          A smooth, general answer that describes what they would do rather than what they actually did. No specific situation, no real person, no moment where it was genuinely hard.
                        </p>
                      </div>
                      <div style={{ background: '#FFF5F5', borderLeft: '3px solid #CC4444', padding: '10px 12px', borderRadius: '0 6px 6px 0', marginBottom: '10px' }}>
                        <div style={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', fontWeight: 700, color: '#CC4444', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                          What a weak answer looks like
                        </div>
                        <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#555', lineHeight: 1.6, margin: 0 }}>
                          Vague. Describes what they would do hypothetically, not what they actually did.
                        </p>
                      </div>
                    </div>
                      );
                    })()}
                    <div style={{ marginTop: '14px', padding: '12px', background: '#F5FAF9', borderRadius: '6px', fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#1A7A6E', fontWeight: 500 }}>
                      🔒 9 more questions with coaching notes — unlock to view
                    </div>
                  </>
                ) : (
                  <>
                    {pack ? (
                      <>
                        {pack.openingNote && (
                          <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', lineHeight: 1.65, color: '#555', marginBottom: '16px' }}>
                            {pack.openingNote}
                          </p>
                        )}
                        {/* Warm-up segment — static, not scored */}
                        <div style={{ marginBottom: '4px', marginTop: '4px' }}>
                          <div style={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', fontWeight: 600, color: '#888888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
                            Warm-up - not scored (~5 min)
                          </div>
                          <div style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: '#555555', marginBottom: '12px' }}>
                            Two easy questions to settle the candidate in. Don't score these. Just note what you hear.
                          </div>
                          {WARMUP_QUESTIONS.map((wq) => (
                            <div key={wq.id} style={{ background: '#FAFAFA', border: '1px solid #E8E8E8', borderRadius: '6px', padding: '12px 14px', marginBottom: '10px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', fontWeight: 700, color: '#1A7A6E', minWidth: '28px' }}>
                                  {wq.id}
                                </span>
                              </div>
                              <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', fontWeight: 600, color: '#1A1A1A', lineHeight: 1.5, margin: 0 }}>
                                {wq.text(role.trim(), company || '')}
                              </p>
                            </div>
                          ))}
                          <div style={{ borderBottom: '1px solid #E0E0E0', margin: '16px 0' }} />
                        </div>
                        {pack.characterPressure && (
                          <div style={{ background: '#1A1A1A', borderRadius: '6px', padding: '12px 16px', marginBottom: '20px' }}>
                            <div style={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', fontWeight: 700, color: '#888', letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: '6px' }}>
                              Character pressure for this hire
                            </div>
                            <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#FFFFFF', lineHeight: 1.5, margin: 0, fontStyle: 'italic' }}>
                              "{pack.characterPressure}"
                            </p>
                          </div>
                        )}
                        {questions.map((q, i) => (
                          <QuestionCard key={i} q={q} index={i} showCoaching={true} />
                        ))}
                        <div style={{ height: 1, background: '#E5E5E5', margin: '24px 0 16px' }} />
                        {pack.complianceNote && (
                          <div style={{ marginBottom: '12px' }}>
                            <div style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '6px' }}>
                              NZ Compliance Note
                            </div>
                            <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#555', lineHeight: 1.6, margin: 0 }}>{pack.complianceNote}</p>
                          </div>
                        )}
                      </>
                    ) : flatQuestions.length > 0 ? (
                      <ol style={{ paddingLeft: '18px', lineHeight: 1.6, fontFamily: 'Inter, sans-serif', color: '#333333' }}>
                        {flatQuestions.map((q, i) => <li key={i} style={{ marginBottom: '6px' }}>{q}</li>)}
                      </ol>
                    ) : null}
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                      <button style={copyButtonStyle} onClick={() => navigator.clipboard.writeText(questions.map(q => q.question).join('\n'))}>
                        Copy questions only
                      </button>
                      <button style={copyButtonStyle} onClick={() => navigator.clipboard.writeText(packCopyText)}>
                        Copy full pack with coaching notes
                      </button>
                    </div>
                  </>
                )}
              </div>
            );
          })()}

          {/* 4. NZ PAY BENCHMARK — salary database lookup */}
          {(() => {
            const dbResult = lookupSalary(role.trim(), location);

            return (
              <div style={{
                background: '#fff',
                border: '1px solid #E5E5E5',
                borderRadius: '10px',
                padding: '20px',
                marginTop: '16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
              }}>
                <h3 style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '16px',
                  fontWeight: 700,
                  borderBottom: '2px solid #1A7A6E',
                  paddingBottom: '6px',
                  marginBottom: '12px',
                  color: '#1A1A1A',
                }}>
                  NZ Pay Benchmark
                </h3>

                {dbResult.found ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#555555' }}>Median</span>
                      <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '18px', fontWeight: 700, color: '#1A7A6E' }}>{dbResult.median}</span>
                    </div>
                    <div style={{ height: 1, background: '#E5E5E5' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#555555' }}>Range</span>
                      <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#333333', fontWeight: 500 }}>{dbResult.range}</span>
                    </div>
                    <div style={{ height: 1, background: '#E5E5E5' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#555555' }}>Confidence</span>
                      <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#333333', fontWeight: 500 }}>{dbResult.confidence}</span>
                    </div>
                    {dbResult.note && (
                      <>
                        <div style={{ height: 1, background: '#E5E5E5' }} />
                        <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: '#777', fontStyle: 'italic' }}>{dbResult.note}</span>
                      </>
                    )}
                    <div style={{ height: 1, background: '#E5E5E5' }} />
                    <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: '#777' }}>Source: {dbResult.source}</span>
                  </div>
                ) : (
                  <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#777', lineHeight: 1.6 }}>
                    We don't yet have verified market data for this role in {location || 'your region'}. When you're setting pay, it's worth checking what similar {role.trim()} roles are advertising on SEEK and Trade Me right now. That's where your candidates are looking too.
                  </p>
                )}
                  {!isUnlocked && dbResult.found && (
                  <div style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#1A7A6E', marginTop: '12px', fontWeight: 500, cursor: 'pointer' }}>
                    View detailed breakdown and sources on unlock
                  </div>
                )}
              </div>
            );
          })()}

          {/* EMAIL GATE — only shown when locked */}
          {!isUnlocked && (
            <div style={{
              background: '#fff',
              border: '1px solid #E5E5E5',
              borderRadius: '10px',
              padding: '20px',
              marginTop: '24px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            }}>
              <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '18px', fontWeight: 600, color: '#1A1A1A', marginBottom: '4px' }}>
                Your hiring kit is ready
              </p>
              <input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid #DDD',
                  marginTop: '6px',
                  boxSizing: 'border-box',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '15px',
                }}
              />
              <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: '#6B7280', margin: '8px 0 0', textAlign: 'center' }}>
                We use this only to send your kit and occasional product updates. Unsubscribe anytime.
              </p>
              <button
                onClick={async () => {
                  if (!(email && onEmailContinue)) return;
                  // Non-blocking lead capture — failure must never prevent gate progression
                  try {
                    await supabase
                      .from('leads')
                      .upsert({
                        email: email.toLowerCase().trim(),
                        role: role.trim() || null,
                        location: location || null,
                        platform: platform || null,
                        salary_provided: !!salary,
                        paid: false,
                        market: 'NZ',
                      }, {
                        onConflict: 'email',
                        ignoreDuplicates: false,
                      });
                    if (attemptId) {
                      await supabase
                        .from('generation_attempts')
                        .update({ email_captured: true })
                        .eq('id', attemptId);
                    }
                  } catch (dbErr) {
                    console.error('Lead capture failed:', dbErr);
                  }
                  track('email_captured', { role: role.trim(), location });
                  onEmailContinue(email, attemptId);
                }}
                style={{
                  background: '#B8860B',
                  color: '#fff',
                  border: 'none',
                  padding: '12px 18px',
                  borderRadius: '8px',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  fontSize: '15px',
                  cursor: 'pointer',
                  marginTop: '12px',
                  width: '100%',
                }}
              >
                Continue
              </button>
            </div>
          )}

        </div>
      )}

      </div>
    </>
  );
}

function UnlockScreen({ onUnlock, role = '', location = '', email = '', attemptId = null, savedJobAd = '', savedQuestions = [], savedInterviewPack = null, savedJobAdData = null }) {
  const [selectedTier, setSelectedTier] = useState('payperuse');
  const [isLoading, setIsLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');

  const handleUnlock = async (priceId) => {
    if (!priceId) {
      // Dev/test fallback — price ID env vars not set, use onUnlock directly
      onUnlock();
      return;
    }
    setIsLoading(true);
    setCheckoutError('');

    // Persist full kit state to sessionStorage before Stripe redirect
    try {
      const kitState = {
        role,
        location,
        email,
        attemptId,
        savedJobAd,
        savedQuestions,
        savedInterviewPack,
        savedJobAdData,
        selectedTier,
        savedAt: Date.now(),
      };
      localStorage.setItem('roleready_kit_state', JSON.stringify(kitState));
    } catch (e) {
      console.error('Failed to save kit state to localStorage:', e);
    }

    let gclid = '';
    try {
      gclid = localStorage.getItem('roleready_gclid') || '';
    } catch (e) {}

    try {
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ priceId, role, location, email, attemptId, gclid, kitId: savedJobAdData?.kit_id || '' }),
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `Server error ${response.status}`);
      }
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        console.error('No checkout URL returned:', data);
        localStorage.removeItem('roleready_kit_state');
        setCheckoutError(data.error || 'Could not start checkout. Please try again.');
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Checkout error:', error);
      localStorage.removeItem('roleready_kit_state');
      setCheckoutError('Could not start checkout. Please try again.');
      setIsLoading(false);
    }
  };

  const payPerHirePriceId = process.env.REACT_APP_STRIPE_PAY_PER_HIRE_PRICE_ID || '';
  const proAnnualPriceId = process.env.REACT_APP_STRIPE_PRO_ANNUAL_PRICE_ID || '';

  return (
    <div style={{ padding: '24px', maxWidth: '640px', margin: '0 auto' }}>

      <h2 style={{ fontFamily: 'Inter, sans-serif', fontSize: '28px', fontWeight: 700, letterSpacing: '-0.4px', color: '#1A1A1A', marginBottom: '16px' }}>
        Your kit is ready
      </h2>

      {/* Outcome-based unlock copy — S14 */}
      <div style={{ marginBottom: '24px' }}>
        <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '15px', fontWeight: 600, color: '#1A1A1A', marginBottom: '10px' }}>
          What you unlock:
        </p>
        {[
          'Questions designed to expose rehearsed answers',
          'Exactly what strong vs weak answers look like, per question',
          'A candidate scorecard so you\'re not relying on gut feel',
          'A pay benchmark so you don\'t second-guess your offer',
        ].map((item, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '8px' }}>
            <span style={{ color: '#1A7A6E', fontWeight: 700, fontSize: '16px', lineHeight: '1.5', flexShrink: 0 }}>✓</span>
            <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '16px', color: '#1A1A1A', lineHeight: 1.5 }}>{item}</span>
          </div>
        ))}
      </div>

      {/* Two-tier pricing — S13 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
        {/* One-Time Hiring Kit */}
        <div
          onClick={() => setSelectedTier('payperuse')}
          style={{
            border: selectedTier === 'payperuse' ? '2px solid #B8860B' : '1px solid #E5E5E5',
            borderRadius: '12px',
            padding: '16px',
            cursor: 'pointer',
            background: selectedTier === 'payperuse' ? '#FFFDF5' : '#fff',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          }}
        >
          <div style={{ display: 'inline-block', background: '#1A1A1A', color: '#fff', fontFamily: 'Inter, sans-serif', fontSize: '10px', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', padding: '2px 8px', borderRadius: '4px', marginBottom: '8px' }}>
            Most popular
          </div>
          <div style={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', fontWeight: 700, color: '#B8860B', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>One-Time Hiring Kit</div>
          <div style={{ fontFamily: 'Inter, sans-serif', fontSize: '28px', fontWeight: 700, color: '#1A1A1A', marginBottom: '4px' }}>$29<span style={{ fontSize: '14px', fontWeight: 400, color: '#555' }}>/hire</span></div>
          <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#555', margin: '0 0 4px', lineHeight: 1.5 }}>Full interview pack, candidate scorecard, job ad, and pay benchmark.</p>
          <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: '#888', margin: 0 }}>Most people start here.</p>
        </div>

        {/* Pro Annual — Coming soon: not clickable, visually muted */}
        <div
          style={{
            border: '1px solid #EEEEEE',
            borderRadius: '12px',
            padding: '16px',
            cursor: 'not-allowed',
            background: '#F9F9F9',
            boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
            opacity: 0.65,
          }}
        >
          <div style={{ display: 'inline-block', background: '#AAAAAA', color: '#fff', fontFamily: 'Inter, sans-serif', fontSize: '10px', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', padding: '2px 8px', borderRadius: '4px', marginBottom: '8px' }}>
            Coming soon
          </div>
          <div style={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', fontWeight: 700, color: '#AAAAAA', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Pro Annual</div>
          <div style={{ fontFamily: 'Inter, sans-serif', fontSize: '28px', fontWeight: 700, color: '#AAAAAA', marginBottom: '4px' }}>$149<span style={{ fontSize: '14px', fontWeight: 400, color: '#AAAAAA' }}>/yr</span></div>
          <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#AAAAAA', margin: '0 0 4px', lineHeight: 1.5 }}>Unlimited hires. All features.</p>
          <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: '#AAAAAA', margin: 0 }}>Hiring more than 5 times a year? This pays for itself fast.</p>
        </div>
      </div>

      <button
        disabled={isLoading}
        onClick={() => handleUnlock(selectedTier === 'proannual' ? proAnnualPriceId : payPerHirePriceId)}
        style={{
          width: '100%',
          background: isLoading ? '#999' : '#B8860B',
          color: '#fff',
          border: 'none',
          padding: '14px',
          borderRadius: '8px',
          fontFamily: 'Inter, sans-serif',
          fontSize: '16px',
          fontWeight: 600,
          cursor: isLoading ? 'not-allowed' : 'pointer',
        }}
      >
        {isLoading ? 'Taking you to payment…' : selectedTier === 'proannual' ? 'Start Pro Annual →' : 'Unlock my hiring kit →'}
      </button>

      {checkoutError && (
        <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#B00020', marginTop: '8px', textAlign: 'center' }}>
          {checkoutError}
        </p>
      )}

      <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: '#777777', marginTop: '12px', textAlign: 'center' }}>
        NZ-compliant content. Instant access after payment.
      </p>
      <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: '#6B7280', marginTop: '8px', textAlign: 'center' }}>
        Not what you needed? Regenerate free or get in touch.
      </p>

    </div>
  );
}

function PrivacyPolicy() {
  return (
    <div style={{maxWidth:'800px',margin:'0 auto',padding:'48px 24px',fontFamily:'Inter,Arial,sans-serif',background:'#F5F5F5',minHeight:'100vh',lineHeight:'1.7'}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'16px 24px',borderBottom:'1px solid #E0E0E0',marginBottom:'32px',marginLeft:'-24px',marginRight:'-24px'}}>
        <span style={{fontSize:'18px',fontWeight:'700',color:'#1A7A6E',letterSpacing:'-0.3px'}}>RoleReady</span>
        <a href="/" style={{fontSize:'14px',color:'#555',textDecoration:'none'}}>← Back to home</a>
      </div>
      <h1 style={{fontSize:'26px',fontWeight:'700',marginBottom:'4px'}}>Privacy Policy</h1>
      <p style={{color:'#666',fontSize:'14px',marginBottom:'40px'}}>RoleReady · Last updated June 2026</p>
      <p style={{fontSize:'14px',color:'#595959'}}>This Privacy Notice for Michael Wilson (doing business as RoleReady) describes how and why we might collect, store, use, and share your personal information when you use our services.</p>
      <h2 style={{fontSize:'16px',fontWeight:'700',marginTop:'32px',textTransform:'uppercase'}}>What information do we collect?</h2>
      <p style={{fontSize:'14px',color:'#595959'}}>We collect names and email addresses you provide directly. Payment data is handled entirely by Stripe and never stored by us. We also automatically collect usage data (IP address, browser type, page views) via Vercel Analytics.</p>
      <h2 style={{fontSize:'16px',fontWeight:'700',marginTop:'32px',textTransform:'uppercase'}}>How do we use your information?</h2>
      <p style={{fontSize:'14px',color:'#595959'}}>To deliver your hiring kit by email, to respond to support requests, to fulfil and manage your orders, to improve our service, and to send marketing communications (you can unsubscribe at any time).</p>
      <h2 style={{fontSize:'16px',fontWeight:'700',marginTop:'32px',textTransform:'uppercase'}}>Who do we share your information with?</h2>
      <p style={{fontSize:'14px',color:'#595959'}}>We share data only with our service providers: Stripe (payments), Resend (email delivery), Supabase (database), Vercel (hosting and analytics), and Anthropic (AI document generation). We do not sell your data.</p>
      <h2 style={{fontSize:'16px',fontWeight:'700',marginTop:'32px',textTransform:'uppercase'}}>AI-based products</h2>
      <p style={{fontSize:'14px',color:'#595959'}}>We use third-party AI services to generate hiring kit content based on your inputs. Content is generated from a question library reviewed by a senior NZ HR practitioner with 15+ years' experience. Your role and region inputs are processed to produce your kit outputs.</p>
      <h2 style={{fontSize:'16px',fontWeight:'700',marginTop:'32px',textTransform:'uppercase'}}>Cookies</h2>
      <p style={{fontSize:'14px',color:'#595959'}}>We use cookies via Vercel Analytics to collect usage data. You can set your browser to reject cookies.</p>
      <h2 style={{fontSize:'16px',fontWeight:'700',marginTop:'32px',textTransform:'uppercase'}}>Your rights (NZ Privacy Act 2020)</h2>
      <p style={{fontSize:'14px',color:'#595959'}}>You have the right to access, correct, or request deletion of your personal information. Email <a href="mailto:hello@roleready.co.nz" style={{color:'#1A7A6E'}}>hello@roleready.co.nz</a>. You may also complain to the Office of the NZ Privacy Commissioner.</p>
      <h2 style={{fontSize:'16px',fontWeight:'700',marginTop:'32px',textTransform:'uppercase'}}>Contact</h2>
      <p style={{fontSize:'14px',color:'#595959'}}>Email: <a href="mailto:hello@roleready.co.nz" style={{color:'#1A7A6E'}}>hello@roleready.co.nz</a></p>
      <p style={{fontSize:'12px',color:'#888',marginTop:'60px',paddingTop:'24px',borderTop:'1px solid #DDD'}}>RoleReady · Michael Wilson trading as RoleReady · roleready.co.nz · Last updated June 2026</p>
    </div>
  );
}

function TermsOfService() {
  return (
    <div style={{maxWidth:'800px',margin:'0 auto',padding:'48px 24px',fontFamily:'Inter,Arial,sans-serif',background:'#F5F5F5',minHeight:'100vh',lineHeight:'1.7'}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'16px 24px',borderBottom:'1px solid #E0E0E0',marginBottom:'32px',marginLeft:'-24px',marginRight:'-24px'}}>
        <span style={{fontSize:'18px',fontWeight:'700',color:'#1A7A6E',letterSpacing:'-0.3px'}}>RoleReady</span>
        <a href="/" style={{fontSize:'14px',color:'#555',textDecoration:'none'}}>← Back to home</a>
      </div>
      <h1 style={{fontSize:'26px',fontWeight:'700',marginBottom:'4px'}}>Terms of Service</h1>
      <p style={{color:'#666',fontSize:'14px',marginBottom:'40px'}}>RoleReady · Last updated June 2026</p>
      <p style={{fontSize:'14px',color:'#595959'}}>These Terms of Service govern your use of RoleReady (roleready.co.nz), operated by Michael Wilson trading as RoleReady. By using RoleReady you agree to these terms.</p>
      <h2 style={{fontSize:'16px',fontWeight:'700',marginTop:'32px',textTransform:'uppercase'}}>The service</h2>
      <p style={{fontSize:'14px',color:'#595959'}}>RoleReady is an online hiring kit tool for New Zealand small business operators. We generate a free job advertisement and offer a paid hiring kit ($29 NZD) containing role-specific interview questions, a candidate scorecard, and a NZ pay benchmark. Kits are delivered as a PDF by email.</p>
      <h2 style={{fontSize:'16px',fontWeight:'700',marginTop:'32px',textTransform:'uppercase'}}>Your use of the service</h2>
      <p style={{fontSize:'14px',color:'#595959'}}>You may use RoleReady for your own business hiring purposes. You agree not to resell or redistribute outputs, use the service for any unlawful purpose, attempt to reverse-engineer the question library or scoring methodology, or submit false or misleading information.</p>
      <h2 style={{fontSize:'16px',fontWeight:'700',marginTop:'32px',textTransform:'uppercase'}}>Payment</h2>
      <p style={{fontSize:'14px',color:'#595959'}}>Payments are processed securely by Stripe. We accept Visa, Mastercard, and American Express in New Zealand Dollars (NZD). We do not store your payment card details. The $29 fee is a one-time charge per hiring kit.</p>
      <h2 style={{fontSize:'16px',fontWeight:'700',marginTop:'32px',textTransform:'uppercase'}}>Refunds</h2>
      <p style={{fontSize:'14px',color:'#595959'}}>If your kit is not useful, email <a href="mailto:hello@roleready.co.nz" style={{color:'#1A7A6E'}}>hello@roleready.co.nz</a> within 7 days of purchase for a full refund.</p>
      <h2 style={{fontSize:'16px',fontWeight:'700',marginTop:'32px',textTransform:'uppercase'}}>What the kit is and isn't</h2>
      <p style={{fontSize:'14px',color:'#595959'}}>RoleReady provides structured hiring guidance built to align with NZ employment law (Employment Relations Act 2000, Human Rights Act 1993). It does not replace advice from a qualified employment lawyer or HR professional for your specific situation.</p>
      <h2 style={{fontSize:'16px',fontWeight:'700',marginTop:'32px',textTransform:'uppercase'}}>Limitation of liability</h2>
      <p style={{fontSize:'14px',color:'#595959'}}>Our liability to you for any claim is limited to the amount you paid for the relevant kit. Hiring decisions remain entirely yours.</p>
      <h2 style={{fontSize:'16px',fontWeight:'700',marginTop:'32px',textTransform:'uppercase'}}>Governing law</h2>
      <p style={{fontSize:'14px',color:'#595959'}}>These terms are governed by New Zealand law. Disputes resolved through informal negotiation first, then arbitration in Christchurch, New Zealand.</p>
      <h2 style={{fontSize:'16px',fontWeight:'700',marginTop:'32px',textTransform:'uppercase'}}>Contact</h2>
      <p style={{fontSize:'14px',color:'#595959'}}><a href="mailto:hello@roleready.co.nz" style={{color:'#1A7A6E'}}>hello@roleready.co.nz</a></p>
      <p style={{fontSize:'12px',color:'#888',marginTop:'60px',paddingTop:'24px',borderTop:'1px solid #DDD'}}>RoleReady · Michael Wilson trading as RoleReady · roleready.co.nz</p>
    </div>
  );
}

function RefundPolicy() {
  return (
    <div style={{maxWidth:'800px',margin:'0 auto',padding:'48px 24px',fontFamily:'Inter,Arial,sans-serif',background:'#F5F5F5',minHeight:'100vh',lineHeight:'1.7'}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'16px 24px',borderBottom:'1px solid #E0E0E0',marginBottom:'32px',marginLeft:'-24px',marginRight:'-24px'}}>
        <span style={{fontSize:'18px',fontWeight:'700',color:'#1A7A6E',letterSpacing:'-0.3px'}}>RoleReady</span>
        <a href="/" style={{fontSize:'14px',color:'#555',textDecoration:'none'}}>← Back to home</a>
      </div>
      <h1 style={{fontSize:'26px',fontWeight:'700',marginBottom:'4px'}}>Refund Policy</h1>
      <p style={{color:'#666',fontSize:'14px',marginBottom:'40px'}}>RoleReady · Last updated June 2026</p>
      <div style={{background:'#F0FDF4',borderLeft:'4px solid #1A7A6E',padding:'16px 20px',borderRadius:'4px',margin:'24px 0'}}>
        <strong style={{fontSize:'14px'}}>The short version:</strong> <span style={{fontSize:'14px',color:'#595959'}}>If your hiring kit isn't useful, email us at hello@roleready.co.nz within 7 days of purchase and we'll give you a full refund. No hoops to jump through.</span>
      </div>
      <h2 style={{fontSize:'16px',fontWeight:'700',marginTop:'32px',textTransform:'uppercase'}}>Refund eligibility</h2>
      <p style={{fontSize:'14px',color:'#595959'}}>You are eligible for a full refund if you request it within 7 days of your purchase date. Email <a href="mailto:hello@roleready.co.nz" style={{color:'#1A7A6E'}}>hello@roleready.co.nz</a> with your order details. We do not require you to prove the kit was defective. If it wasn't what you needed, that's enough.</p>
      <h2 style={{fontSize:'16px',fontWeight:'700',marginTop:'32px',textTransform:'uppercase'}}>How to request a refund</h2>
      <p style={{fontSize:'14px',color:'#595959'}}>Email <a href="mailto:hello@roleready.co.nz" style={{color:'#1A7A6E'}}>hello@roleready.co.nz</a> with the email address you used to purchase, the role and region you generated the kit for, and a brief note that you'd like a refund. We will process within 5 business days to the original payment method.</p>
      <h2 style={{fontSize:'16px',fontWeight:'700',marginTop:'32px',textTransform:'uppercase'}}>When refunds don't apply</h2>
      <p style={{fontSize:'14px',color:'#595959'}}>More than 7 days have passed since your purchase, or the kit was delivered successfully and you have already used it to hire.</p>
      <h2 style={{fontSize:'16px',fontWeight:'700',marginTop:'32px',textTransform:'uppercase'}}>Technical issues</h2>
      <p style={{fontSize:'14px',color:'#595959'}}>If your kit was not delivered or had a technical fault, we will re-deliver or issue a full refund, your choice. Email <a href="mailto:hello@roleready.co.nz" style={{color:'#1A7A6E'}}>hello@roleready.co.nz</a>.</p>
      <h2 style={{fontSize:'16px',fontWeight:'700',marginTop:'32px',textTransform:'uppercase'}}>Contact</h2>
      <p style={{fontSize:'14px',color:'#595959'}}><a href="mailto:hello@roleready.co.nz" style={{color:'#1A7A6E'}}>hello@roleready.co.nz</a></p>
      <p style={{fontSize:'12px',color:'#888',marginTop:'60px',paddingTop:'24px',borderTop:'1px solid #DDD'}}>RoleReady · Michael Wilson trading as RoleReady · roleready.co.nz</p>
    </div>
  );
}

function FAQPage() {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      q: "How do I run a good job interview if I've never done it before?",
      a: "Most small business owners haven't, and it's usually where hiring goes wrong. The good news is you don't need training. You need a bit of structure. Work out the few things the role really needs before you sit down, ask every person the same questions, and write down what they actually said rather than how they made you feel. The trap is the confident talker. They come across well, you walk out with a good feeling, then three weeks later you're wondering what you missed. Same questions for everyone and a simple way to score the answers is what stops you hiring on a hunch."
    },
    {
      q: "What questions should I ask when interviewing for a specific role?",
      a: "The best questions are about real situations the person has actually handled, not what they reckon they'd do. \"Tell me about a time a customer kicked off and how you handled it\" is far harder to fake than \"how would you deal with a difficult customer.\" The questions also need to fit the job. What you ask a kitchen hand is nothing like what you ask someone running your office. Generic questions get you generic answers and leave you no wiser. RoleReady builds a question set for the actual role you're hiring, with notes on what a good answer sounds like so you know it when you hear it."
    },
    {
      q: "How can I tell if someone is exaggerating or giving rehearsed answers?",
      a: "It comes down to detail. A real answer has the specifics in it: what actually happened, what they did, how it turned out, usually with a rough edge or two. A rehearsed answer sounds smooth but stays vague, all the right words like \"communication\" and \"teamwork\" with no actual story underneath. When something sounds too tidy, ask a follow-up. \"What did you actually say to them?\" or \"what would you do differently next time?\" Someone who lived it can go deeper. Someone running a script tends to stall."
    },
    {
      q: "How do I compare candidates fairly instead of going on gut feel?",
      a: "Score them on the same things, while the interview is still fresh in your head, not at the end of the day when they've all blurred into one. Pick the four or five things that genuinely matter for the job, rate each person on each, then compare the scores instead of your overall vibe. Gut feel is where you trip up. The person most like you, or the smoothest talker, feels like the best pick even when they're not right for the work. A scorecard doesn't take your judgement away. It just makes sure you're weighing everyone the same way, on what counts."
    },
    {
      q: "What should I pay someone in NZ for a particular role?",
      a: "Start with what the role is being advertised at right now in your region, not a nationwide average, which can be well off for your area. Pay shifts with location, how senior the role is, and how hard that skill is to find at the moment. The minimum wage is the legal floor, but for most roles you'll need to sit well above it to attract anyone good. RoleReady gives you a current pay benchmark for your specific role and region, with a median, a range, and a confidence rating, so you're not pulling a number out of thin air or working off something three years out of date."
    },
    {
      q: "Should I put the pay rate in my job ad?",
      a: "Usually, yes. Ads with a pay rate pull more applicants and better-matched ones, because people sort themselves out before they apply. You're not wasting an afternoon interviewing someone after twice your budget. Leaving pay off tends to make people assume it's low even when it isn't. The one exception is when you honestly haven't landed on a number yet, and even then it's better to leave it out cleanly than write \"competitive salary,\" which tells a candidate nothing and most have learned to skim straight past. If you do put a range, make it one you'd actually pay."
    },
    {
      q: "How do I set a salary range for a new role?",
      a: "Anchor it to what the role is going for in your region right now, then build your range around where you'd realistically land for the right person. Keep it tight enough to mean something, because a $20k spread tells a candidate nothing, but with enough room to pay more for more experience. Remember the real cost sits above the wage too, once you add KiwiSaver, ACC, and holiday pay. If you're not sure where the market is at, a benchmark for that specific role and region is the quickest way to set a range you can stand behind with a candidate and still afford."
    },
    {
      q: "How do I write a job ad that attracts the right people?",
      a: "Be specific and be honest. The ads that work describe the real role, the real team, and what the job is actually like, including the hard bits, so the wrong people rule themselves out before they apply. Vague ads stuffed with \"fast-paced environment\" and \"team player\" pull in everyone and tell no one anything. Lead with what makes the role worth going for, say plainly what you need, and put the pay in. A good ad does some of the sifting for you. The right person reads it and thinks \"that's me\" and the wrong one keeps scrolling."
    },
    {
      q: "Where should I post a job in NZ, SEEK or Trade Me Jobs?",
      a: "Both are the main options here, and which one works better comes down to the role. SEEK usually has the wider reach for office, professional, and skilled roles. Trade Me Jobs often works better for trades, hospitality, retail, and anything where you're hiring local. For a lot of small business roles it's worth putting it on both. The same ad works either way, though the layout differs a bit. SEEK uses set fields and search highlights, while Trade Me leads with your first couple of lines. RoleReady formats your ad ready to paste straight into whichever you go with."
    },
    {
      q: "Do I legally have to put anything specific in a NZ job ad?",
      a: "There's no long checklist for the ad itself, but you do have to steer clear of discrimination. Under the Human Rights Act 1993 you can't word an ad to screen people out on things like age, sex, ethnicity, religion, family status, or disability, so \"young and energetic\" or \"recent graduate preferred\" can land you in hot water. You can state genuine job requirements, including the right to work in NZ. The heavier legal obligations turn up later, at the offer and agreement stage. RoleReady ads are written to line up with the Human Rights Act so you don't trip over the wording without realising."
    },
    {
      q: "What does a bad hire actually cost a small business?",
      a: "More than most owners realise, and most of it you don't see coming. There's the obvious part, the wages paid for work that wasn't up to scratch plus having to recruit all over again, but the bigger cost is the time you pour into managing the problem, the drag on everyone else, the customers affected, and the weeks gone before you're back to square one. On a small team, one wrong hire is felt by the whole crew. That's the case for slowing down at the interview. A careful process costs you very little next to getting it wrong and starting again."
    },
    {
      q: "How do I avoid hiring the wrong person?",
      a: "Bad hires nearly always trace back to a rushed process. Interviewing on gut, asking everyone different things, picking whoever came across best on the day rather than whoever can actually do the job. The fixes aren't complicated. Know what the role needs before you start, ask everyone the same real-world questions, listen for genuine detail over polish, score them the same way, and check references before you make an offer. Simple enough on paper, but hard to pull off in your head halfway through an interview. A structured kit is really just a way to make sure you don't skip the steps that catch the wrong hire."
    },
    {
      q: "Can't I just use ChatGPT to write my interview questions and job ad?",
      a: "You can, and it'll hand you something. There's just a gap between something and something you'd stake a hire on. A general AI tool writes you plausible-looking questions, but it doesn't know which ones actually tell you anything for that role, it won't tell you what a strong answer sounds like versus a rehearsed one, and it won't flag where your wording could fall foul of NZ employment law. You're left judging the quality yourself, which is the bit you wanted a hand with in the first place. RoleReady is built for NZ hiring specifically. The questions come from a library reviewed by a senior NZ HR practitioner with more than 15 years' experience, the pay data is local, and the ads are written to line up with the Human Rights Act. It's the difference between a generic draft and a kit you can hire off."
    },
    {
      q: "Why do I need a scorecard? Can't I just remember who was best?",
      a: "Memory is the problem. By the time you've interviewed three or four people over a couple of days, you're often comparing your notes on one candidate with your impression of another. The early candidates have blurred and the last one feels freshest. The person who interviewed most recently, or who you clicked with personally, tends to feel like the strongest, even when they weren't the strongest on the things that actually matter for the job. A scorecard fixes this by having you rate each person on the same criteria immediately after the interview, while it's still fresh. When you compare scores at the end, you're comparing what they actually said against the same standard, not your impression of them three days later. For a small team where one wrong hire affects everyone, that consistency is worth more than it sounds. You're still making the call. The scorecard just makes sure you're making it on evidence rather than on who happened to interview last."
    }
  ];

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '48px 24px', fontFamily: 'Inter, sans-serif', background: '#F5F5F5', minHeight: '100vh', lineHeight: 1.7 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', borderBottom: '1px solid #E0E0E0', marginBottom: '32px', marginLeft: '-24px', marginRight: '-24px' }}>
        <span style={{ fontSize: '18px', fontWeight: 700, color: '#1A7A6E', letterSpacing: '-0.3px' }}>RoleReady</span>
        <a href="/" style={{ fontSize: '14px', color: '#555', textDecoration: 'none' }}>← Back to home</a>
      </div>
      <h1 style={{ fontSize: '28px', fontWeight: 700, marginBottom: '4px', color: '#1A1A1A' }}>Common questions</h1>
      <p style={{ color: '#555', fontSize: '15px', marginBottom: '40px' }}>Straight answers to what NZ employers actually ask before they hire.</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {faqs.map((faq, index) => (
          <div key={index} style={{ borderBottom: '1px solid #E5E5E5' }}>
            <button
              onClick={() => setOpenIndex(openIndex === index ? null : index)}
              style={{ width: '100%', background: 'none', border: 'none', padding: '18px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, cursor: 'pointer', textAlign: 'left' }}
            >
              <span style={{ fontFamily: 'Inter, sans-serif', fontSize: 16, fontWeight: 600, color: '#1A1A1A', lineHeight: 1.4 }}>{faq.q}</span>
              <span style={{ fontFamily: 'Inter, sans-serif', fontSize: 20, color: '#1A7A6E', flexShrink: 0, lineHeight: 1, marginTop: 2, transform: openIndex === index ? 'rotate(45deg)' : 'none', transition: 'transform 0.2s ease', display: 'inline-block' }}>+</span>
            </button>
            {openIndex === index && (
              <div style={{ paddingBottom: 20, paddingRight: 32 }}>
                <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 15, color: '#444444', lineHeight: 1.7, margin: 0 }}>{faq.a}</p>
              </div>
            )}
          </div>
        ))}
      </div>
      <div style={{ marginTop: '48px', paddingTop: '32px', borderTop: '1px solid #E0E0E0', textAlign: 'center' }}>
        <a href="/?start=1" style={{ display: 'inline-block', background: '#B8860B', color: '#FFFFFF', textDecoration: 'none', borderRadius: 6, padding: '14px 32px', fontFamily: 'Inter, sans-serif', fontSize: 16, fontWeight: 600 }}>Get started free →</a>
      </div>
    </div>
  );
}
function useSEOHead(title, description) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;

    let metaDesc = document.querySelector('meta[name="description"]');
    const created = !metaDesc;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    const prevDescription = metaDesc.getAttribute('content');
    metaDesc.setAttribute('content', description);

    return () => {
      document.title = prevTitle;
      if (created) {
        metaDesc.remove();
      } else if (prevDescription !== null) {
        metaDesc.setAttribute('content', prevDescription);
      }
    };
  }, [title, description]);
}

function SEOClosingBand({ label = 'Get your hiring kit — $29' }) {
  return (
    <section style={{ background: '#1A7A6E', padding: '48px 24px', textAlign: 'center' }}>
      <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 22, fontWeight: 700, color: '#FFFFFF', margin: '0 0 8px', letterSpacing: '-0.3px' }}>
        Ready to sort your next hire?
      </p>
      <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 15, color: '#EAF4F2', margin: '0 0 24px' }}>
        Free to start. One-off $29. No subscription.
      </p>
      <a href="/?start=1" style={{ display: 'inline-block', background: '#B8860B', color: '#FFFFFF', textDecoration: 'none', borderRadius: 6, padding: '14px 32px', fontFamily: 'Inter, sans-serif', fontSize: 16, fontWeight: 600 }}>
        {label}
      </a>
    </section>
  );
}

function SEOPageShell({ children, ctaLabel }) {
  return (
    <div style={{ fontFamily: 'Inter, sans-serif' }}>
      <Navbar scrolled={true} onCtaClick={() => { window.location.href = '/?start=1'; }} />
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '48px 24px 0', background: '#F5F5F5' }}>
        <div style={{ marginBottom: '32px' }}>
          <a href="/" style={{ fontSize: '14px', color: '#555', textDecoration: 'none' }}>← Back to home</a>
        </div>
        {children}
      </div>
      <div style={{ background: '#F5F5F5', height: '48px' }} />
      <SEOClosingBand label={ctaLabel} />
    </div>
  );
}

const seoH1 = { fontSize: '28px', fontWeight: 700, marginBottom: '8px', color: '#1A1A1A', letterSpacing: '-0.3px' };
const seoSub = { fontSize: '15px', color: '#555', marginBottom: '32px', lineHeight: 1.6 };
const seoH2 = { fontSize: '18px', fontWeight: 700, marginTop: '36px', marginBottom: '10px', color: '#1A1A1A' };
const seoBody = { fontSize: '15px', color: '#444444', lineHeight: 1.7, marginBottom: '14px' };
const seoBullet = { fontSize: '15px', color: '#444444', lineHeight: 1.7, marginBottom: '8px', paddingLeft: '0' };
const seoCallout = { background: '#FFFFFF', border: '1px solid #E5E5E5', borderRadius: 8, padding: '20px', margin: '20px 0' };
const seoCredLine = { fontSize: '13px', color: '#888', fontStyle: 'italic', marginTop: '20px' };

// ----------------------------------------------------------------------------
// PAGE 1 — /how-it-works
// ----------------------------------------------------------------------------
function HowItWorksPage() {
  useSEOHead(
    'How It Works — Your Hiring Kit in 15 Minutes | RoleReady',
    'See how RoleReady turns four quick questions into a complete NZ hiring kit: job ad, interview pack, candidate scorecard and pay benchmark, ready in about 15 minutes.'
  );
  return (
    <SEOPageShell>
      <h1 style={seoH1}>Your next hire. Sorted in 15 minutes.</h1>
      <p style={seoSub}>No HR background needed. No recruiter required. Answer a few questions about the role and get everything you need to hire properly.</p>

      <p style={seoBody}>Most NZ small business owners wing it when they hire. They post a vague ad, run the interview on gut feel, and hope for the best. RoleReady gives you the tools that remove the guesswork. Built on a question and scoring library shaped by a senior NZ HR practitioner with 15+ years' experience.</p>

      <h2 style={seoH2}>Here's how it works</h2>

      <div style={seoCallout}>
        <p style={{ ...seoBody, marginBottom: '4px', fontWeight: 600, color: '#1A7A6E' }}>Step 1 — Tell us the role</p>
        <p style={{ ...seoBody, marginBottom: 0 }}>The role title and where you're hiring. That's the only thing we actually need. A line or two about your team or what makes the role different sharpens everything that follows, but it's optional.</p>
      </div>

      <div style={seoCallout}>
        <p style={{ ...seoBody, marginBottom: '4px', fontWeight: 600, color: '#1A7A6E' }}>Step 2 — Get your free job ad</p>
        <p style={{ ...seoBody, marginBottom: 0 }}>RoleReady generates a ready-to-post job ad, formatted for SEEK NZ or Trade Me Jobs. Specific enough that the right people apply and the wrong ones don't. This part is free — you see it before you pay anything.</p>
      </div>

      <div style={seoCallout}>
        <p style={{ ...seoBody, marginBottom: '4px', fontWeight: 600, color: '#1A7A6E' }}>Step 3 — Unlock your interview pack</p>
        <p style={{ ...seoBody, marginBottom: 0 }}>Role-specific interview questions with coaching notes on what a strong answer sounds like, what a weak one looks like, and how to spot the rehearsed one. Considers NZ employment law including the Human Rights Act 1993 — not a substitute for legal advice.</p>
      </div>

      <div style={seoCallout}>
        <p style={{ ...seoBody, marginBottom: '4px', fontWeight: 600, color: '#1A7A6E' }}>Step 4 — Compare your candidates</p>
        <p style={{ ...seoBody, marginBottom: 0 }}>The candidate scorecard lets you rate each person against the same criteria, so your decision comes down to evidence, not who interviewed best on the day.</p>
      </div>

      <h2 style={seoH2}>What's in the $29 hiring kit</h2>
      <p style={seoBullet}>• <strong>Job ad</strong> — ready to post on SEEK or Trade Me</p>
      <p style={seoBullet}>• <strong>Interview pack</strong> — role-specific questions with coaching notes, built on a library reviewed by a senior NZ HR practitioner</p>
      <p style={seoBullet}>• <strong>Candidate scorecard</strong> — score and compare every candidate on the same criteria</p>
      <p style={seoBullet}>• <strong>NZ pay benchmark</strong> — the going rate for your role, RoleReady NZ Pay Ranges · June 2026 review</p>

      <p style={{ ...seoBody, marginTop: '20px' }}>One-off payment. No subscription. Delivered as a PDF, ready to use the same day.</p>

      <p style={seoCredLine}>Built on a question and scoring library reviewed by a senior NZ HR practitioner with 15+ years' experience. Considers NZ employment law including the Human Rights Act 1993 - not a substitute for legal advice. June 2026 review.</p>

    </SEOPageShell>
  );
}

// ----------------------------------------------------------------------------
// PAGE 2 — /interview-questions
// ----------------------------------------------------------------------------
function InterviewQuestionsPage() {
  useSEOHead(
    'NZ Interview Questions & Candidate Scorecard | RoleReady',
    'Structured interview questions for NZ small business hiring, with coaching notes on strong vs rehearsed answers, plus a candidate scorecard to compare fairly.'
  );
  return (
    <SEOPageShell ctaLabel="Get the full interview pack - $29">
      <h1 style={seoH1}>Know exactly what to ask. Know what a good answer sounds like.</h1>
      <p style={seoSub}>The interview is where you pick the right person. Most NZ small business owners go in without a plan and end up hiring the confident talker. Here's how to stop that.</p>

      <p style={seoBody}>The single biggest hiring mistake is winging the interview. You ask the same three questions you always ask, someone gives a polished answer, and you hire them. Six weeks later you know it was wrong.</p>

      <p style={seoBody}>RoleReady's interview pack gives you structured questions built for your specific role, drawn from a question and scoring library reviewed by a senior NZ HR practitioner with 15+ years' experience.</p>

      <h2 style={seoH2}>Three questions from the pack — so you know what you're getting</h2>

      <div style={seoCallout}>
        <p style={{ ...seoBody, fontWeight: 600, marginBottom: '14px' }}>Q1 — Tell me about a time you had to manage something going wrong when you were short-staffed. What did you do?</p>
        <div style={{ background: '#F5FAF9', borderLeft: '3px solid #1A7A6E', padding: '10px 12px', borderRadius: '0 6px 6px 0' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#1A7A6E', textTransform: 'uppercase', marginBottom: '4px' }}>What you're listening for</div>
          <p style={{ fontSize: '13px', color: '#333', lineHeight: 1.6, margin: 0 }}>A specific situation, a real decision, and an honest outcome. The rehearsed version will be vague or perfect. Push for the detail.</p>
        </div>
      </div>

      <div style={seoCallout}>
        <p style={{ ...seoBody, fontWeight: 600, marginBottom: '14px' }}>Q2 — What does a good day in this role look like for you?</p>
        <div style={{ background: '#F5FAF9', borderLeft: '3px solid #1A7A6E', padding: '10px 12px', borderRadius: '0 6px 6px 0' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#1A7A6E', textTransform: 'uppercase', marginBottom: '4px' }}>What you're listening for</div>
          <p style={{ fontSize: '13px', color: '#333', lineHeight: 1.6, margin: 0 }}>They should be able to describe the actual job, not the idea of the job. Vague answers about "helping people" or "being part of a team" tell you they haven't thought it through.</p>
        </div>
      </div>

      <div style={seoCallout}>
        <p style={{ ...seoBody, fontWeight: 600, marginBottom: '14px' }}>Q3 — Is there anything about the way this role is set up that doesn't suit how you work?</p>
        <div style={{ background: '#F5FAF9', borderLeft: '3px solid #1A7A6E', padding: '10px 12px', borderRadius: '0 6px 6px 0' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#1A7A6E', textTransform: 'uppercase', marginBottom: '4px' }}>What you're listening for</div>
          <p style={{ fontSize: '13px', color: '#333', lineHeight: 1.6, margin: 0 }}>Self-awareness. Someone who can answer honestly is someone who knows themselves. Someone who says "nothing at all, it sounds perfect" probably hasn't read the description.</p>
        </div>
      </div>

      <h2 style={seoH2}>What the interview pack includes</h2>
      <p style={seoBullet}>• <strong>Role-specific questions</strong>, not a generic list. Written for the actual job you're filling, whether that's a café manager, a labourer, a receptionist or a retail supervisor.</p>
      <p style={seoBullet}>• <strong>Coaching notes on every question</strong>: what a strong answer sounds like, what a weak one looks like, and how to spot the rehearsed one that tells you nothing.</p>
      <p style={seoBullet}>• <strong>Behavioural questions that get past the prep</strong> - built to surface real detail, not a rehearsed pitch.</p>
      <p style={seoBullet}>• <strong>The candidate scorecard</strong> - rate each candidate against the same criteria immediately after the interview, and compare side by side.</p>

      <h2 style={seoH2}>Don't get fooled twice</h2>
      <p style={seoBody}>If you've made a bad hire before, you already know the cost — not just in money but in time, stress and disruption to the rest of your team. A structured interview doesn't guarantee the right hire. It gives you a much better chance of spotting the wrong one.</p>

      <p style={seoCredLine}>Interview pack built on a question library reviewed by a senior NZ HR practitioner with 15+ years' experience. Considers NZ employment law including the Human Rights Act 1993 - not a substitute for legal advice. June 2026 review.</p>

    </SEOPageShell>
  );
}

// ----------------------------------------------------------------------------
// PAGE 3 — /sample
// ----------------------------------------------------------------------------
function SamplePage() {
  useSEOHead(
    'See a Sample Hiring Kit | RoleReady',
    'A real sample from RoleReady: job ad extract, interview question with coaching notes, and candidate scorecard criteria, built for a café manager role in Auckland.'
  );
  return (
    <SEOPageShell>
      <h1 style={seoH1}>See exactly what you get before you buy.</h1>
      <p style={seoSub}>A real example from the RoleReady kit, built for a café manager role. Your kit is generated for your specific role and region.</p>

      <div style={{ background: '#F5FAF9', border: '1px solid #D5E8E4', borderRadius: 8, padding: '16px 20px', marginBottom: '24px' }}>
        <p style={{ fontSize: '14px', color: '#1A7A6E', margin: 0, fontWeight: 500 }}>Want to see your own role instead of this sample? Run a free personalised sample from the homepage — you'll see your job ad before you pay anything.</p>
      </div>

      <h2 style={seoH2}>Sample — Job ad extract</h2>
      <div style={seoCallout}>
        <p style={{ ...seoBody, fontWeight: 700, marginBottom: '10px' }}>Café Manager — Auckland CBD, Full Time</p>
        <p style={seoBody}>Small café in central Auckland, 250+ covers a day. Looking for a manager who can run the floor and hold standards without hand-holding.</p>
        <p style={{ ...seoBody, fontWeight: 600, marginBottom: '4px' }}>What you'll do:</p>
        <p style={seoBullet}>• Lead daily operations — open, close, floor, and team</p>
        <p style={seoBullet}>• Manage rostering, stock, and supplier relationships</p>
        <p style={seoBullet}>• Hold quality and service standards across every shift</p>
        <p style={{ ...seoBody, marginTop: '10px', marginBottom: 0, color: '#888', fontStyle: 'italic' }}>Pay: included in your kit as the NZ pay benchmark for this role - RoleReady NZ Pay Ranges · June 2026 review.</p>
      </div>

      <h2 style={seoH2}>Sample — Interview question extract</h2>
      <div style={seoCallout}>
        <p style={{ ...seoBody, fontWeight: 700, marginBottom: '10px' }}>Walk me through a time you turned around an underperforming shift.</p>
        <div style={{ background: '#F5FAF9', borderLeft: '3px solid #1A7A6E', padding: '10px 12px', borderRadius: '0 6px 6px 0', marginBottom: '8px' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#1A7A6E', textTransform: 'uppercase', marginBottom: '4px' }}>Strong answer</div>
          <p style={{ fontSize: '13px', color: '#333', lineHeight: 1.6, margin: 0 }}>Names the specific problem, the action they took, and the measurable shift in the team.</p>
        </div>
        <div style={{ background: '#FFF8EC', borderLeft: '3px solid #B8860B', padding: '10px 12px', borderRadius: '0 6px 6px 0' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#B8860B', textTransform: 'uppercase', marginBottom: '4px' }}>Watch for the rehearsed answer</div>
          <p style={{ fontSize: '13px', color: '#555', lineHeight: 1.6, margin: 0 }}>A perfectly rehearsed answer that sounds confident but contains no real detail about the actual people or situation involved.</p>
        </div>
      </div>

      <h2 style={seoH2}>Sample — Scorecard extract</h2>
      <div style={seoCallout}>
        <div style={{ background: '#EBF5FB', borderLeft: '2px solid #1B4F72', padding: '8px 12px', fontWeight: 700, fontSize: '13px', color: '#1B4F72', marginBottom: '8px' }}>Team leadership and standards</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          <div style={{ background: '#FADBDB', padding: '8px 10px', borderRadius: 4 }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#C0392B', marginBottom: '3px' }}>No (1)</div>
            <div style={{ fontSize: '11px', color: '#444' }}>Cannot describe running a shift without a manager present.</div>
          </div>
          <div style={{ background: '#FDEBD0', padding: '8px 10px', borderRadius: 4 }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#E67E22', marginBottom: '3px' }}>Maybe (2)</div>
            <div style={{ fontSize: '11px', color: '#444' }}>Has covered shifts but always with someone senior nearby.</div>
          </div>
          <div style={{ background: '#D5F5E3', padding: '8px 10px', borderRadius: 4 }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#1A7A6E', marginBottom: '3px' }}>Yes (3)</div>
            <div style={{ fontSize: '11px', color: '#444' }}>Gives a clear example of running a shift independently and handling what came up.</div>
          </div>
          <div style={{ background: '#D6EAF8', padding: '8px 10px', borderRadius: 4 }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#1B4F72', marginBottom: '3px' }}>Strong Yes (4)</div>
            <div style={{ fontSize: '11px', color: '#444' }}>Has designed shift procedures that others now follow.</div>
          </div>
        </div>
      </div>

      <p style={seoBody}>Every kit includes a complete job ad, an interview pack of role-specific questions with coaching notes, a candidate scorecard, and the NZ pay benchmark for your role. All generated for the specific position you're filling.</p>

      <p style={seoCredLine}>Built on a question and scoring library reviewed by a senior NZ HR practitioner with 15+ years' experience. Considers NZ employment law including the Human Rights Act 1993 - not a substitute for legal advice. June 2026 review.</p>

    </SEOPageShell>
  );
}

// ----------------------------------------------------------------------------
// PAGE 4 — /pricing
// ----------------------------------------------------------------------------
function SEOPricingPage() {
  useSEOHead(
    'Pricing — $29 Hiring Kit, No Subscription | RoleReady',
    '$29 one-off for a complete NZ hiring kit — job ad, interview pack, candidate scorecard and pay benchmark. Or $149/year unlimited. No monthly fees.'
  );
  return (
    <SEOPageShell>
      <h1 style={seoH1}>Our pricing is simple.</h1>
      <p style={seoSub}>$29 for the complete hiring kit. One-off. No subscription. Or $149/year if you're hiring often.</p>

      <h2 style={seoH2}>What's included for $29</h2>
      <p style={seoBullet}>• <strong>Job ad</strong> — ready to post on SEEK or Trade Me</p>
      <p style={seoBullet}>• <strong>Interview pack</strong> — role-specific questions with coaching notes</p>
      <p style={seoBullet}>• <strong>Candidate scorecard</strong> — score and compare on the same criteria</p>
      <p style={seoBullet}>• <strong>NZ pay benchmark</strong> — the going rate for your role, RoleReady NZ Pay Ranges · June 2026 review</p>

      <p style={seoBody}>Generated for your specific role. Delivered as a PDF. Ready to use the same day.</p>

      <h2 style={seoH2}>How this compares</h2>
      <p style={seoBody}>A recruitment agency typically charges 10–15% of the first year's salary. RoleReady is a one-off $29.</p>
      <p style={seoBody}>An HR software subscription starts from around $9 per employee per month, before payroll or add-ons — a running cost for tools you only need when you're actually hiring. RoleReady is $29 and you only pay when you need it.</p>
      <p style={seoBody}>A bad hire costs more than either. The kit won't guarantee the right person. It gives you a much better chance of spotting the wrong one.</p>

      <h2 style={seoH2}>Who it's for</h2>
      <p style={seoBody}>RoleReady is built for NZ small business owners making their next hire — trades, retail, hospitality, professional services. If you've got between 2 and 20 staff and you hire reactively rather than through an HR team, this is for you.</p>
      <p style={seoBody}>It's not for large companies with dedicated recruitment functions, and it's not a substitute for ongoing HR support. It's for the business owner who needs to hire someone, wants to do it properly, and doesn't want to pay a recruiter to do it for them.</p>

      <h2 style={seoH2}>The two options</h2>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: '16px' }}>
        <div style={{ background: '#FFFDF5', border: '2px solid #B8860B', borderRadius: 12, padding: '20px' }}>
          <div style={{ display: 'inline-block', background: '#1A1A1A', color: '#fff', fontSize: '10px', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', padding: '2px 8px', borderRadius: 4, marginBottom: '10px' }}>Most popular</div>
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#B8860B', textTransform: 'uppercase', marginBottom: '4px' }}>One-Time Hiring Kit</div>
          <div style={{ fontSize: '28px', fontWeight: 700, color: '#1A1A1A', marginBottom: '6px' }}>$29<span style={{ fontSize: '14px', fontWeight: 400, color: '#555' }}>/hire</span></div>
          <p style={{ fontSize: '13px', color: '#555', margin: 0, lineHeight: 1.5 }}>Full interview pack, candidate scorecard, job ad, and pay benchmark. Not useful? Email us within 7 days for a full refund.</p>
        </div>
        <div style={{ background: '#F9F9F9', border: '1px solid #EEEEEE', borderRadius: 12, padding: '20px', opacity: 0.65 }}>
          <div style={{ display: 'inline-block', background: '#AAAAAA', color: '#fff', fontSize: '10px', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', padding: '2px 8px', borderRadius: 4, marginBottom: '10px' }}>Coming soon</div>
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#AAAAAA', textTransform: 'uppercase', marginBottom: '4px' }}>Pro Annual</div>
          <div style={{ fontSize: '28px', fontWeight: 700, color: '#AAAAAA', marginBottom: '6px' }}>$149<span style={{ fontSize: '14px', fontWeight: 400, color: '#AAAAAA' }}>/yr</span></div>
          <p style={{ fontSize: '13px', color: '#AAAAAA', margin: 0, lineHeight: 1.5 }}>Unlimited hires. All features. Hiring more than 5 times a year? This pays for itself fast.</p>
        </div>
      </div>

      <p style={seoCredLine}>Built on a question and scoring library reviewed by a senior NZ HR practitioner with 15+ years' experience. Considers NZ employment law including the Human Rights Act 1993 - not a substitute for legal advice. June 2026 review.</p>

    </SEOPageShell>
  );
}
function App() {
  const [screen, setScreen] = useState(() => {
    if (window.location.pathname === '/success') return 'landing';
    if (new URLSearchParams(window.location.search).get('start') === '1') return 'generator';
    try {
      const saved = localStorage.getItem('roleready_kit_state');
      if (saved) {
        const kitState = JSON.parse(saved);
        if (Date.now() - kitState.savedAt < 7200000) {
          return 'results';
        }
      }
    } catch (e) {}
    return 'landing';
  });
  const urlParams = new URLSearchParams(window.location.search);
  const previewMode = urlParams.get('preview') === 'kiwi2026';

  const [isUnlocked, setIsUnlocked] = useState(() => {
    if (window.location.pathname === '/success') return false;
    try {
      const saved = localStorage.getItem('roleready_kit_state');
      if (saved) {
        const kitState = JSON.parse(saved);
        if (Date.now() - kitState.savedAt < 7200000) return true;
      }
    } catch (e) {}
    return false;
  });
    useEffect(() => {
    if (previewMode) {
      console.log('[DEV] Paywall bypass active via ?preview=true');
    }
    // Capture Google Ads click ID on arrival so it survives the Stripe round-trip.
    // Stored once and never overwritten with a blank, so a later internal navigation
    // (which has no gclid) cannot wipe the original ad-click ID.
    try {
      const gclid = new URLSearchParams(window.location.search).get('gclid');
      if (gclid) {
        localStorage.setItem('roleready_gclid', gclid);
      }
    } catch (e) {}
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [userEmail, setUserEmail] = useState(''); // eslint-disable-line no-unused-vars
  const [paidReturnMessage, setPaidReturnMessage] = useState(false);

  const getInitialKitState = () => {
    // Do not read localStorage on the /success page — the key must survive until
    // the user navigates back to / and App mounts there
    if (window.location.pathname === '/success') return null;
    try {
      const saved = localStorage.getItem('roleready_kit_state');
      if (saved) {
        const k = JSON.parse(saved);
        if (Date.now() - k.savedAt < 7200000) return k;
      }
    } catch (e) {}
    return null;
  };

  const _initialKit = getInitialKitState();

  const [savedRole, setSavedRole] = useState(_initialKit?.role || '');
  const [savedLocation, setSavedLocation] = useState(_initialKit?.location || '');
  const [savedJobAd, setSavedJobAd] = useState(_initialKit?.savedJobAd || '');
  const [savedQuestions, setSavedQuestions] = useState(_initialKit?.savedQuestions || []);
  const [savedSalary, setSavedSalary] = useState(null);
  const [savedInterviewPack, setSavedInterviewPack] = useState(_initialKit?.savedInterviewPack || null);
  const [savedJobAdData, setSavedJobAdData] = useState(_initialKit?.savedJobAdData || null);
  const [savedAttemptId, setSavedAttemptId] = useState(_initialKit?.attemptId || null);

  useEffect(() => {
    // Do not restore or remove kit state while on the /success page
    if (window.location.pathname === '/success') return;


    // Legacy stripeSessionId banner (kept for backwards compat)
    const paidSession = sessionStorage.getItem('stripeSessionId');
    if (paidSession) {
      sessionStorage.removeItem('stripeSessionId');
      setPaidReturnMessage(true);
      try {
        const _kt = JSON.parse(localStorage.getItem('roleready_kit_state') || '{}');
        track('kit_purchased', {
          role: _kt.role || '',
          location: _kt.location || '',
          tier: _kt.selectedTier === 'proannual' ? 'pro_annual' : 'pay_per_hire',
        });
      } catch (e) {}
    }

    // ── Email the kit PDF once, on confirmed paid return ───────────────────
    // Fires only when a real Stripe session just returned (paidSession truthy),
    // builds the SAME PDF as the download button, and posts it to the
    // send-kit-email endpoint for delivery as an attachment. Guarded per
    // session_id so a refresh or re-mount cannot send twice.
    if (paidSession) {
      try {
        const emailedKey = 'roleready_kit_emailed_' + paidSession;
        if (!localStorage.getItem(emailedKey)) {
          const _kt = JSON.parse(localStorage.getItem('roleready_kit_state') || '{}');
          const _email = _kt.email;
          const _role = (_kt.role || '').trim();
          const _location = _kt.location || '';
          const _jobAdData = _kt.savedJobAdData || null;
          const _interviewPack = _kt.savedInterviewPack || null;

          if (_email && _role) {
            const slugForCriteria = toRoleSlug(_role);
            const criteria = getCriteriaForRole(slugForCriteria);
            const dbResult = lookupSalary(_role, _location);

            const { base64, filename } = getHiringKitPDFBase64({
              role: _role,
              location: _location,
              businessName: _kt.company || '',
              jobAd: _kt.savedJobAd || '',
              jobAdData: _jobAdData,
              platform: _jobAdData?.platform || 'seek',
              scorecardCriteria: criteria,
              apiScorecardCriteria: _jobAdData?.scorecardCriteria ?? null,
              screeningCriteria: _jobAdData?.screeningCriteria ?? null,
              interviewPack: _interviewPack,
              salaryData: dbResult.found ? dbResult : null,
            });

            // Mark as emailed BEFORE the network call so a slow/double mount
            // cannot double-send. If the send fails the customer still has the
            // in-app download; we do not retry automatically.
            localStorage.setItem(emailedKey, '1');

            fetch('/api/send-kit-email', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email: _email, role: _role, location: _location, pdfBase64: base64, filename }),
            }).catch((err) => console.error('Kit email send failed (non-blocking):', err));
          }
        }
      } catch (e) {
        console.error('Kit email build failed (non-blocking):', e);
      }
    }

    // Restore full kit content state on return from Stripe
    // (screen and isUnlocked already set by lazy useState initialisers above)
    const saved = localStorage.getItem('roleready_kit_state');
    if (saved) {
      try {
        const kitState = JSON.parse(saved);
        if (kitState && Date.now() - kitState.savedAt < 7200000) {
          if (kitState.role) setSavedRole(kitState.role);
          if (kitState.location) setSavedLocation(kitState.location);
          if (kitState.email) setUserEmail(kitState.email);
          if (kitState.savedJobAd) setSavedJobAd(kitState.savedJobAd);
          if (kitState.savedQuestions) setSavedQuestions(kitState.savedQuestions);
          if (kitState.savedInterviewPack) setSavedInterviewPack(kitState.savedInterviewPack);
          if (kitState.savedJobAdData) setSavedJobAdData(kitState.savedJobAdData);
          if (kitState.attemptId) setSavedAttemptId(kitState.attemptId);
          localStorage.removeItem('roleready_kit_state');
        }
      } catch (e) {
        console.error('Failed to restore kit state:', e);
        localStorage.removeItem('roleready_kit_state');
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [screen]);

  if (window.location.pathname === '/success') {
    return <Success />;
  }

  if (window.location.pathname === '/privacy-policy') return <PrivacyPolicy />;
  if (window.location.pathname === '/terms-of-service') return <TermsOfService />;
  if (window.location.pathname === '/refund-policy') return <RefundPolicy />;
  if (window.location.pathname === '/faq') return <FAQPage />;
  if (window.location.pathname === '/how-it-works') return <HowItWorksPage />;
if (window.location.pathname === '/interview-questions') return <InterviewQuestionsPage />;
if (window.location.pathname === '/sample') return <SamplePage />;
if (window.location.pathname === '/pricing') return <SEOPricingPage />;

  return (
    <>
      {/* Fixed top header — consistent across all screens */}
      <header style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: '48px',
        background: '#F5F5F5',
        borderBottom: '1px solid #E0E0E0',
        display: 'flex',
        alignItems: 'center',
        padding: '0 20px',
        zIndex: 1000,
      }}>
        <span style={{
          fontFamily: 'Inter, sans-serif',
          fontWeight: 600,
          fontSize: '16px',
          color: '#1A1A1A',
        }}>RoleReady</span>
      </header>
      <div style={{ paddingTop: '48px' }}>
      {paidReturnMessage && (
        <div style={{
          background: '#E8F5E9',
          border: '1px solid #1A7A6E',
          borderRadius: '8px',
          padding: '16px 20px',
          margin: '16px',
          color: '#1A7A6E',
          fontSize: '15px',
          fontFamily: 'Inter, sans-serif',
        }}>
          <strong>Payment confirmed</strong>, your full hiring kit is unlocked below.
        </div>
      )}
      {screen === 'landing' && <LandingScreen onCtaClick={() => setScreen('generator')} />}
      {screen === 'generator' && (
        <GeneratorScreen
          isUnlocked={isUnlocked}
          onEmailContinue={(email, aid) => {
            setUserEmail(email);
            if (aid) setSavedAttemptId(aid);
            setScreen('unlock');
          }}

          onGenerationComplete={(role, location, jobAd, questions, salary, interviewPack, jobAdData) => {
  setSavedRole(role);
  setSavedLocation(location);
  setSavedJobAd(jobAd);
  setSavedQuestions(questions);
  setSavedSalary(salary);
  setSavedInterviewPack(interviewPack || null);
  setSavedJobAdData(jobAdData || null);
  if (previewMode) {
    setIsUnlocked(true);
  }
  window.scrollTo(0, 0);
}}
        />
      )}
      {screen === 'unlock' && (
        <UnlockScreen
          onUnlock={() => {
            setIsUnlocked(true);
            setScreen('results');
          }}
          role={savedRole}
          location={savedLocation}
          email={userEmail}
          attemptId={savedAttemptId}
          savedJobAd={savedJobAd}
          savedQuestions={savedQuestions}
          savedInterviewPack={savedInterviewPack}
          savedJobAdData={savedJobAdData}
        />
      )}
      {screen === 'results' && (
        <GeneratorScreen
          isUnlocked={true}
          onEmailContinue={(email) => {
            setUserEmail(email);
            setScreen('unlock');
          }}
          initialRole={savedRole}
          initialLocation={savedLocation}
          initialJobAd={savedJobAd}
          initialQuestions={savedQuestions}
          initialSalary={savedSalary}
          initialInterviewPack={savedInterviewPack}
          initialJobAdData={savedJobAdData}
        />
      )}
      </div>
      <Analytics />
    </>
  );
}

export default App;
